import React,{useState,useRef,useCallback,useEffect} from 'react';
import {TeamChatView} from '../components/Chat';
import {AwardAchievementModal} from '../components/PlayerBase';
import {AchievementBadges,DevPointsPanel,Flag,MatchKeyInfo,PlayerDevelopmentSection,PlayerPhotoUploader,StatsFilterMenu,useStatsVisibility} from '../components/Shared';
import {api} from '../lib/api';
import {DRAWING_COLORS,FORMATIONS,STATUS_COLORS} from '../lib/constants';
import {fmtClock,fmtDate,fmtDateShort,fmtMins,generateFormationSlots,liveClockSeconds,livePlayerSeconds,mkSlots,renderStaticShape} from '../lib/utils';

export function DrawingToolbar({tool,setTool,color,setColor,onClear,hasDrawings,ac}){
  return(
    <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
      {[["move","↔ Move"],["arrow-solid","➜ Arrow"],["arrow-dashed","┈➜ Dashed Arrow"],["line-solid","／ Line"],["line-dashed","┈ Dashed Line"],["circle","◯ Circle"],["text","🔤 Text"],["oppplayer","🧍 Opponent"],["ghostplayer","👻 Ghost Player"],["eraser","🧹 Erase"]].map(([v,l])=>(
        <button key={v} onClick={()=>setTool(v)} style={{padding:"6px 10px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:tool===v?ac:"var(--c-bg0)",color:tool===v?"#fff":"var(--c-muted)"}}>{l}</button>
      ))}
      <div style={{display:"flex",gap:4,marginLeft:4}}>
        {DRAWING_COLORS.map(c=>(
          <button key={c} onClick={()=>setColor(c)} title={c} style={{width:22,height:22,borderRadius:"50%",border:color===c?"2px solid var(--c-text)":"2px solid transparent",background:c,cursor:"pointer",boxShadow:"inset 0 0 0 1px rgba(0,0,0,0.15)"}}/>
        ))}
      </div>
      {hasDrawings&&<button onClick={onClear} style={{marginLeft:"auto",padding:"6px 10px",borderRadius:6,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer"}}>Clear All</button>}
    </div>
  );
}

// The "5v5 / 6v6 / ... / Formation…" row — normally rendered by Pitch itself right
// above the board, but pulled out as its own component so a page that also has a
// drawing toolbar (the Strategy tab) can place it wherever makes sense in that
// page's layout instead of always being pinned immediately above the pitch. Pass
// `hideFormatRow` to <Pitch/> when rendering this separately, so it isn't shown twice.
export function PitchFormatRow({slots,setSlots,sideCount,setSideCount,tc}){
  function changeSide(n){const ns=mkSlots(n);slots.forEach((s,i)=>{if(ns[i])ns[i].playerId=s.playerId;});setSideCount(n);setSlots(ns);}
  return(
    <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
      {[5,6,7,9,11].map(n=><button key={n} onClick={()=>changeSide(n)} style={{padding:"4px 10px",borderRadius:5,cursor:"pointer",fontSize:11,fontWeight:700,background:sideCount===n?tc:"var(--c-bg0)",color:sideCount===n?"#fff":"var(--c-muted)",border:sideCount===n?`1px solid ${tc}`:"1px solid var(--c-border)"}}>{n}v{n}</button>)}
      {FORMATIONS[sideCount]&&<select onChange={e=>{if(!e.target.value)return;const preset=FORMATIONS[sideCount].find(f=>f.name===e.target.value);if(!preset)return;const newPositions=generateFormationSlots(preset.lines);setSlots(prev=>newPositions.map((np,i)=>({...np,playerId:prev[i]?.playerId||null})));e.target.value="";}} defaultValue="" style={{padding:"4px 8px",borderRadius:5,fontSize:11,fontWeight:600,background:"var(--c-bg0)",color:"var(--c-muted)",border:"1px solid var(--c-border)",cursor:"pointer"}}>
        <option value="">Formation…</option>
        {FORMATIONS[sideCount].map(f=><option key={f.name} value={f.name}>{f.name}</option>)}
      </select>}
      <span style={{marginLeft:"auto",fontSize:9,color:"var(--c-muted2)"}}>Drag • Click to assign</span>
    </div>
  );
}

export function Pitch({players,slots,setSlots,tc,sideCount,setSideCount,subs,setSubs,readOnly,tool,color,drawings,setDrawings,maxWidth,hideFormatRow}){
  const [drag,setDrag]=useState(null);const [pick,setPick]=useState(null);
  const [showAddSub,setShowAddSub]=useState(false);
  const [draft,setDraft]=useState(null); // in-progress drawn shape while dragging
  const ref=useRef(null);const off=useRef({x:0,y:0});const dd=useRef(false);
  function changeSide(n){const ns=mkSlots(n);slots.forEach((s,i)=>{if(ns[i])ns[i].playerId=s.playerId;});setSideCount(n);setSlots(ns);setPick(null);}
  const ass=slots.map(s=>s.playerId).filter(Boolean);
  const subsList=subs||[];
  function addSub(pid){ if(setSubs)setSubs(prev=>[...(prev||[]),pid]); setShowAddSub(false); }
  function removeSub(pid){ if(setSubs)setSubs(prev=>(prev||[]).filter(id=>id!==pid)); }
  const availableForSub=players.filter(p=>!ass.includes(p.id)&&!subsList.includes(p.id));
  const drawingMode=tool&&tool!=="move";
  function pd(e,id){if(drawingMode)return;e.preventDefault();e.stopPropagation();dd.current=false;const p=ref.current.getBoundingClientRect();const s=slots.find(s=>s.id===id);const cx=e.touches?e.touches[0].clientX:e.clientX;const cy=e.touches?e.touches[0].clientY:e.clientY;off.current={x:cx-p.left-(s.x/100)*p.width,y:cy-p.top-(s.y/100)*p.height};off.current.startX=cx;off.current.startY=cy;setDrag(id);setPick(null);}
  const pm=useCallback(e=>{if(drag===null)return;const p=ref.current?.getBoundingClientRect();if(!p)return;const cx=e.touches?e.touches[0].clientX:e.clientX;const cy=e.touches?e.touches[0].clientY:e.clientY;const dist=Math.hypot(cx-(off.current.startX||cx),cy-(off.current.startY||cy));if(dist>8)dd.current=true;if(!dd.current)return;if(e.cancelable)e.preventDefault();const x=Math.min(95,Math.max(5,((cx-p.left-off.current.x)/p.width)*100));const y=Math.min(96,Math.max(4,((cy-p.top-off.current.y)/p.height)*100));setSlots(prev=>prev.map(s=>s.id===drag?{...s,x,y}:s));},[drag]);
  const pu=useCallback(()=>{if(drag!==null&&!dd.current)setPick(id=>id===drag?null:drag);setDrag(null);},[drag]);
  useEffect(()=>{window.addEventListener("mousemove",pm);window.addEventListener("mouseup",pu);window.addEventListener("touchmove",pm,{passive:false});window.addEventListener("touchend",pu);return()=>{window.removeEventListener("mousemove",pm);window.removeEventListener("mouseup",pu);window.removeEventListener("touchmove",pm);window.removeEventListener("touchend",pu);};},[pm,pu]);
  function asgn(sid,pid){setSlots(prev=>prev.map(s=>{if(s.playerId===pid)return{...s,playerId:null};if(s.id===sid)return{...s,playerId:pid};return s;}));if(setSubs)setSubs(prev=>(prev||[]).filter(id=>id!==pid));setPick(null);}

  // ── Tactical drawing (arrows/circles/text) — only active when a drawing tool is selected ──
  function pctFromEvent(e){
    const p=ref.current.getBoundingClientRect();
    const cx=e.touches?e.touches[0].clientX:e.clientX;
    const cy=e.touches?e.touches[0].clientY:e.clientY;
    return {x:((cx-p.left)/p.width)*100,y:((cy-p.top)/p.height)*130};
  }
  function eraseNearest(list,pos){
    let bestIdx=-1,bestDist=9;
    list.forEach((d,i)=>{
      let dist=999;
      if(d.type==="text"||d.type==="oppplayer"||d.type==="ghostplayer")dist=Math.hypot(d.x-pos.x,d.y-pos.y);
      else if(d.type==="circle")dist=Math.abs(Math.hypot(d.cx-pos.x,d.cy-pos.y)-d.r);
      else dist=Math.hypot((d.x1+d.x2)/2-pos.x,(d.y1+d.y2)/2-pos.y);
      if(dist<bestDist){bestDist=dist;bestIdx=i;}
    });
    return bestIdx===-1?list:list.filter((_,i)=>i!==bestIdx);
  }
  function drawDown(e){
    if(!drawingMode)return;
    e.preventDefault();
    const pos=pctFromEvent(e);
    if(tool==="text"){
      const t=window.prompt("Annotation text:");
      if(t&&t.trim()&&setDrawings)setDrawings(prev=>[...(prev||[]),{id:"d"+Date.now(),type:"text",x:pos.x,y:pos.y,text:t.trim().slice(0,40),color:color||"#ffffff",size:5}]);
      return;
    }
    if(tool==="oppplayer"){
      if(setDrawings)setDrawings(prev=>[...(prev||[]),{id:"d"+Date.now(),type:"oppplayer",x:pos.x,y:pos.y}]);
      return;
    }
    if(tool==="ghostplayer"){
      if(setDrawings)setDrawings(prev=>[...(prev||[]),{id:"d"+Date.now(),type:"ghostplayer",x:pos.x,y:pos.y,color:color||"#ffffff"}]);
      return;
    }
    if(tool==="eraser"){
      if(setDrawings)setDrawings(prev=>eraseNearest(prev||[],pos));
      return;
    }
    setDraft({type:tool,x1:pos.x,y1:pos.y,x2:pos.x,y2:pos.y});
  }
  const drawMove=useCallback(e=>{
    if(!draft)return;
    if(e.cancelable)e.preventDefault();
    const pos=pctFromEvent(e);
    setDraft(prev=>prev&&({...prev,x2:pos.x,y2:pos.y}));
  },[draft]);
  const drawUp=useCallback(()=>{
    if(!draft)return;
    const {type,x1,y1,x2,y2}=draft;
    const dist=Math.hypot(x2-x1,y2-y1);
    if(dist>1.5&&setDrawings){
      if(type==="circle")setDrawings(prev=>[...(prev||[]),{id:"d"+Date.now(),type:"circle",cx:x1,cy:y1,r:dist,color:color||"#ffffff"}]);
      else if(type==="line-solid"||type==="line-dashed")setDrawings(prev=>[...(prev||[]),{id:"d"+Date.now(),type:"line",x1,y1,x2,y2,style:type==="line-dashed"?"dashed":"solid",color:color||"#ffffff"}]);
      else setDrawings(prev=>[...(prev||[]),{id:"d"+Date.now(),type:"arrow",x1,y1,x2,y2,style:type==="arrow-dashed"?"dashed":"solid",color:color||"#ffffff"}]);
    }
    setDraft(null);
  },[draft,color,setDrawings]);
  useEffect(()=>{
    if(!drawingMode)return;
    window.addEventListener("mousemove",drawMove);window.addEventListener("mouseup",drawUp);
    window.addEventListener("touchmove",drawMove,{passive:false});window.addEventListener("touchend",drawUp);
    return()=>{window.removeEventListener("mousemove",drawMove);window.removeEventListener("mouseup",drawUp);window.removeEventListener("touchmove",drawMove);window.removeEventListener("touchend",drawUp);};
  },[drawingMode,drawMove,drawUp]);

  // ── Moving an already-drawn shape (arrow/circle/text) — active in Move mode ──
  const [dragDrawingId,setDragDrawingId]=useState(null);
  const dragDrawingStart=useRef(null);
  const pinchStateRef=useRef(null); // {id, initialDist, initialSize} while pinch-resizing a text element
  function getTouchDistance(e){
    if(!e.touches||e.touches.length<2)return null;
    const t1=e.touches[0],t2=e.touches[1];
    return Math.hypot(t2.clientX-t1.clientX,t2.clientY-t1.clientY);
  }
  function getTouchMidpointPct(e){
    const p=ref.current.getBoundingClientRect();
    const t1=e.touches[0],t2=e.touches[1];
    const mx=(t1.clientX+t2.clientX)/2,my=(t1.clientY+t2.clientY)/2;
    return {x:((mx-p.left)/p.width)*100,y:((my-p.top)/p.height)*130};
  }
  useEffect(()=>{
    function onTouchMove(e){
      if(!pinchStateRef.current)return;
      if(e.touches&&e.touches.length===2){
        if(e.cancelable)e.preventDefault();
        const dist=getTouchDistance(e);
        const {id,initialDist,initialSize}=pinchStateRef.current;
        if(dist&&initialDist&&setDrawings){
          const newSize=Math.max(3,Math.min(20,initialSize*(dist/initialDist)));
          setDrawings(prev=>prev.map(d=>d.id===id?{...d,size:newSize}:d));
        }
      }
    }
    function onTouchEnd(){ pinchStateRef.current=null; }
    window.addEventListener("touchmove",onTouchMove,{passive:false});
    window.addEventListener("touchend",onTouchEnd);
    window.addEventListener("touchcancel",onTouchEnd);
    return()=>{window.removeEventListener("touchmove",onTouchMove);window.removeEventListener("touchend",onTouchEnd);window.removeEventListener("touchcancel",onTouchEnd);};
  },[setDrawings]);
  function findNearestDrawing(list,pos,threshold){
    let bestIdx=-1,bestDist=threshold;
    list.forEach((d,i)=>{
      let dist=999;
      if(d.type==="text"||d.type==="oppplayer"||d.type==="ghostplayer")dist=Math.hypot(d.x-pos.x,d.y-pos.y);
      else if(d.type==="circle")dist=Math.abs(Math.hypot(d.cx-pos.x,d.cy-pos.y)-d.r);
      else dist=Math.hypot((d.x1+d.x2)/2-pos.x,(d.y1+d.y2)/2-pos.y);
      if(dist<bestDist){bestDist=dist;bestIdx=i;}
    });
    return bestIdx;
  }
  const dragMovedRef=useRef(false);
  function boardPointerDown(e){
    // Two fingers down on (or already dragging) a text element → pinch-resize instead of drag/scroll.
    if(e.touches&&e.touches.length===2){
      let targetId=null,targetSize=5;
      if(dragDrawingId&&dragDrawingStart.current?.orig?.type==="text"){
        targetId=dragDrawingId;targetSize=dragDrawingStart.current.orig.size||5;
      } else if(!drawingMode&&drawings&&drawings.length>0){
        const pos=getTouchMidpointPct(e);
        const idx=findNearestDrawing(drawings,pos,10);
        if(idx!==-1&&drawings[idx].type==="text"){targetId=drawings[idx].id;targetSize=drawings[idx].size||5;}
      }
      if(targetId){
        e.preventDefault();
        setDragDrawingId(null);
        dragDrawingStart.current=null;
        pinchStateRef.current={id:targetId,initialDist:getTouchDistance(e),initialSize:targetSize};
        return;
      }
    }
    if(drawingMode){drawDown(e);return;}
    if(!setDrawings||!drawings||drawings.length===0)return;
    const pos=pctFromEvent(e);
    const idx=findNearestDrawing(drawings,pos,8);
    if(idx===-1)return;
    e.preventDefault();
    e.stopPropagation();
    const d=drawings[idx];
    dragMovedRef.current=false;
    setDragDrawingId(d.id);
    dragDrawingStart.current={pos,orig:d};
  }
  const dragDrawingMove=useCallback(e=>{
    if(!dragDrawingId)return;
    if(e.cancelable)e.preventDefault();
    const pos=pctFromEvent(e);
    const start=dragDrawingStart.current;
    if(!start)return;
    const dx=pos.x-start.pos.x,dy=pos.y-start.pos.y;
    if(Math.hypot(dx,dy)>1.2)dragMovedRef.current=true;
    setDrawings(prev=>prev.map(d=>{
      if(d.id!==dragDrawingId)return d;
      const o=start.orig;
      if(o.type==="text"||o.type==="oppplayer"||o.type==="ghostplayer")return {...o,x:o.x+dx,y:o.y+dy};
      if(o.type==="circle")return {...o,cx:o.cx+dx,cy:o.cy+dy};
      return {...o,x1:o.x1+dx,y1:o.y1+dy,x2:o.x2+dx,y2:o.y2+dy};
    }));
  },[dragDrawingId,setDrawings]);
  const dragDrawingUp=useCallback(()=>{
    if(dragDrawingId&&!dragMovedRef.current&&setDrawings){
      const start=dragDrawingStart.current;
      if(start&&start.orig.type==="text"){
        const answer=window.prompt("Text size (3–14):",String(start.orig.size||5));
        if(answer!==null){
          const newSize=Math.max(3,Math.min(14,parseFloat(answer)||start.orig.size||5));
          setDrawings(prev=>prev.map(d=>d.id===dragDrawingId?{...d,size:newSize}:d));
        }
      }
    }
    setDragDrawingId(null);
    dragDrawingStart.current=null;
  },[dragDrawingId,setDrawings]);
  useEffect(()=>{
    if(!dragDrawingId)return;
    window.addEventListener("mousemove",dragDrawingMove);window.addEventListener("mouseup",dragDrawingUp);
    window.addEventListener("touchmove",dragDrawingMove,{passive:false});window.addEventListener("touchend",dragDrawingUp);
    return()=>{window.removeEventListener("mousemove",dragDrawingMove);window.removeEventListener("mouseup",dragDrawingUp);window.removeEventListener("touchmove",dragDrawingMove);window.removeEventListener("touchend",dragDrawingUp);};
  },[dragDrawingId,dragDrawingMove,dragDrawingUp]);
  function arrowHeadPts(x1,y1,x2,y2,size){
    const angle=Math.atan2(y2-y1,x2-x1);
    const a1=angle+Math.PI*0.85,a2=angle-Math.PI*0.85;
    return `${x2},${y2} ${x2+size*Math.cos(a1)},${y2+size*Math.sin(a1)} ${x2+size*Math.cos(a2)},${y2+size*Math.sin(a2)}`;
  }
  function renderShape(d,key,faded){
    if(d.type==="circle")return <circle key={key} cx={d.cx} cy={d.cy} r={d.r} fill="none" stroke={d.color} strokeWidth="0.8" opacity={faded?0.6:1}/>;
    if(d.type==="text")return <text key={key} x={d.x} y={d.y} fontSize={d.size||5} textAnchor="middle" dominantBaseline="central" fontWeight="700" fill={d.color} stroke="#000" strokeWidth="0.4" paintOrder="stroke" opacity={faded?0.6:1}>{d.text}</text>;
    if(d.type==="line")return <line key={key} x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke={d.color} strokeWidth="0.8" strokeDasharray={d.style==="dashed"?"3 2.5":undefined} opacity={faded?0.6:1}/>;
    if(d.type==="oppplayer")return(
      <g key={key} transform={`translate(${d.x},${d.y})`} opacity={faded?0.35:0.55}>
        <circle cx="0" cy="-3.4" r="1.7" fill="#9ca3af" stroke="#374151" strokeWidth="0.3"/>
        <path d="M -3 3.2 Q -3 -1.6 0 -1.6 Q 3 -1.6 3 3.2 Z" fill="#9ca3af" stroke="#374151" strokeWidth="0.3"/>
      </g>
    );
    if(d.type==="ghostplayer")return(
      <g key={key} transform={`translate(${d.x},${d.y})`} opacity={faded?0.25:0.4}>
        <circle cx="0" cy="0" r="3.2" fill={d.color||"#ffffff"} stroke="#fff" strokeWidth="0.3" strokeDasharray="1 0.8"/>
      </g>
    );
    return(<g key={key} opacity={faded?0.6:1}>
      <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke={d.color} strokeWidth="0.8" strokeDasharray={d.style==="dashed"?"3 2.5":undefined}/>
      <polygon points={arrowHeadPts(d.x1,d.y1,d.x2,d.y2,3)} fill={d.color}/>
    </g>);
  }

  return(<div>
    {!hideFormatRow&&(
      <div style={{display:"flex",gap:5,marginBottom:9,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:"var(--c-muted)",fontWeight:600}}>Format:</span>
        {[5,6,7,9,11].map(n=><button key={n} onClick={()=>changeSide(n)} style={{padding:"4px 10px",borderRadius:5,cursor:"pointer",fontSize:11,fontWeight:700,background:sideCount===n?tc:"var(--c-bg0)",color:sideCount===n?"#fff":"var(--c-muted)",border:sideCount===n?`1px solid ${tc}`:"1px solid var(--c-border)"}}>{n}v{n}</button>)}
        {FORMATIONS[sideCount]&&<select onChange={e=>{if(!e.target.value)return;const preset=FORMATIONS[sideCount].find(f=>f.name===e.target.value);if(!preset)return;const newPositions=generateFormationSlots(preset.lines);setSlots(prev=>newPositions.map((np,i)=>({...np,playerId:prev[i]?.playerId||null})));e.target.value="";}} defaultValue="" style={{padding:"4px 8px",borderRadius:5,fontSize:11,fontWeight:600,background:"var(--c-bg0)",color:"var(--c-muted)",border:"1px solid var(--c-border)",cursor:"pointer"}}>
          <option value="">Formation…</option>
          {FORMATIONS[sideCount].map(f=><option key={f.name} value={f.name}>{f.name}</option>)}
        </select>}
        <span style={{marginLeft:"auto",fontSize:9,color:"var(--c-muted2)"}}>Drag • Click to assign</span>
      </div>
    )}
    <div style={{maxWidth:maxWidth||420,margin:"0 auto"}}>
    <div ref={ref} onClick={()=>setPick(null)} onMouseDown={boardPointerDown} onTouchStart={boardPointerDown} style={{position:"relative",width:"100%",paddingBottom:"130%",background:"linear-gradient(180deg,#14532d 0%,#15803d 50%,#14532d 100%)",borderRadius:9,border:"2px solid #15803d",overflow:"hidden",userSelect:"none",touchAction:drawingMode?"none":"pan-y",cursor:drag!==null?"grabbing":drawingMode?"crosshair":dragDrawingId?"grabbing":"default"}}>
      <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 100 130" preserveAspectRatio="none">
        <rect x="5" y="4" width="90" height="122" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
        <line x1="5" y1="65" x2="95" y2="65" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
        <circle cx="50" cy="65" r="11" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
        <rect x="25" y="4" width="50" height="16" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.4"/>
        <rect x="25" y="110" width="50" height="16" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.4"/>
      </svg>
      {drawings&&(
        <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 100 130" preserveAspectRatio="none">
          {drawings.map(d=>renderShape(d,d.id,false))}
          {draft&&renderShape({...draft,type:draft.type==="circle"?"circle":draft.type.startsWith("line")?"line":"arrow",style:draft.type.endsWith("dashed")?"dashed":"solid",color:color||"#ffffff"},"draft",true)}
        </svg>
      )}
      {slots.map(sl=>{const pl=players.find(p=>p.id===sl.playerId);const isO=pick===sl.id;const isD=drag===sl.id;const flipUp=sl.y>62;return(<div key={sl.id} style={{position:"absolute",left:`${sl.x}%`,top:`${sl.y}%`,transform:"translate(-50%,-50%)",zIndex:isD?100:isO?90:10,transition:isD?"none":"left 0.12s,top 0.12s"}}>
        <div onMouseDown={e=>pd(e,sl.id)} onTouchStart={e=>pd(e,sl.id)} onClick={e=>e.stopPropagation()} style={{width:36,height:36,borderRadius:"50%",background:pl?tc:"var(--c-surface)",border:pl?`2px solid ${tc}cc`:"2px dashed var(--c-muted2)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:isD?"grabbing":"grab",boxShadow:isD?"0 6px 20px rgba(0,0,0,0.5)":"0 2px 6px rgba(0,0,0,0.4)",touchAction:"none"}}>
          {pl?(<><span style={{fontSize:10,fontWeight:800,color:"#fff",lineHeight:1}}>{pl.number}</span><span style={{fontSize:6,color:"var(--c-text3)",maxWidth:32,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"center"}}>{pl.name.split(" ")[0]}</span></>):<span style={{fontSize:13,color:"var(--c-muted2)"}}>+</span>}
        </div>
        {isO&&<div onClick={e=>e.stopPropagation()} style={{position:"absolute",...(flipUp?{bottom:42}:{top:42}),left:"50%",transform:"translateX(-50%)",background:"var(--c-surface)",border:"1px solid var(--c-border)",borderRadius:8,minWidth:185,maxHeight:190,overflowY:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.6)",zIndex:200,padding:"4px 0"}}>
          <div style={{padding:"4px 9px 3px",fontSize:9,color:"var(--c-text)",fontWeight:700,textTransform:"uppercase",borderBottom:"1px solid var(--c-border)",marginBottom:2}}>Assign Player</div>
          {pl&&<button onClick={()=>{setSlots(prev=>prev.map(s=>s.id===sl.id?{...s,playerId:null}:s));setPick(null);}} style={{width:"100%",padding:"5px 9px",background:"none",border:"none",color:"#ef4444",fontSize:11,cursor:"pointer",textAlign:"left"}}>✕ Remove</button>}
          {players.map(p=>{const busy=ass.includes(p.id)&&p.id!==sl.playerId;const sel=p.id===sl.playerId;return(<button key={p.id} onClick={()=>!busy&&asgn(sl.id,p.id)} style={{width:"100%",padding:"5px 9px",background:sel?tc+"33":"none",border:"none",color:busy?"var(--c-muted2)":"var(--c-text)",fontSize:11,cursor:busy?"default":"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:6,opacity:busy?0.5:1}}>
            <span style={{width:5,height:5,borderRadius:"50%",background:STATUS_COLORS[p.status].dot,flexShrink:0}}/><span style={{fontWeight:700,color:"var(--c-text2)",minWidth:18}}>#{p.number}</span><span style={{flex:1}}><Flag value={p.nationality}/> {p.name}</span><span style={{fontSize:9,color:"var(--c-muted)"}}>{p.position}</span>{sel&&<span style={{fontSize:9,color:tc}}>✓</span>}
          </button>);})}
        </div>}
      </div>);})}
    </div>
    </div>
    {!readOnly&&setSubs&&(
      <div style={{marginTop:12}}>
        <div style={{display:"flex",alignItems:"center",marginBottom:7}}>
          <div style={{fontSize:9,color:"var(--c-text)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",flex:1}}>Substitutes ({subsList.length})</div>
          <button onClick={()=>setShowAddSub(s=>!s)} style={{fontSize:10,fontWeight:700,color:tc,background:"none",border:"none",cursor:"pointer",padding:"2px 4px"}}>{showAddSub?"Close":"+ Add"}</button>
        </div>
        {subsList.length===0?(
          <div style={{fontSize:10,color:"var(--c-muted2)",fontStyle:"italic"}}>No substitutes added yet.</div>
        ):(
          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
            {subsList.map(pid=>{const p=players.find(pl=>pl.id===pid);if(!p)return null;return(
              <div key={pid} style={{display:"flex",alignItems:"center",gap:5,background:"var(--c-bg0)",borderRadius:6,padding:"4px 6px 4px 9px",border:"1px solid var(--c-border)"}}>
                <span style={{fontSize:9,fontWeight:700,color:"var(--c-muted)"}}>#{p.number}</span>
                <span style={{fontSize:10,fontWeight:600}}>{p.name.split(" ")[0]}</span>
                <button onClick={()=>removeSub(pid)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:12,padding:"0 2px",lineHeight:1}}>✕</button>
              </div>
            );})}
          </div>
        )}
        {showAddSub&&(
          <div style={{marginTop:8,background:"var(--c-surface)",border:"1px solid var(--c-border)",borderRadius:8,maxHeight:170,overflowY:"auto"}}>
            {availableForSub.length===0?(
              <div style={{padding:10,fontSize:10,color:"var(--c-muted2)"}}>No more players available.</div>
            ):availableForSub.map(p=>(
              <button key={p.id} onClick={()=>addSub(p.id)} style={{width:"100%",padding:"7px 9px",background:"none",border:"none",borderBottom:"1px solid var(--c-border)",color:"var(--c-text)",fontSize:11,cursor:"pointer",textAlign:"left",display:"flex",gap:6,alignItems:"center"}}>
                <span style={{width:5,height:5,borderRadius:"50%",background:STATUS_COLORS[p.status].dot,flexShrink:0}}/><span style={{fontWeight:700,color:"var(--c-muted)",minWidth:20}}>#{p.number}</span><span style={{flex:1}}><Flag value={p.nationality}/> {p.name}</span><span style={{fontSize:9,color:"var(--c-muted)"}}>{p.position}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )}
    {readOnly&&subsList.length>0&&(
      <div style={{marginTop:12}}>
        <div style={{fontSize:9,color:"var(--c-text)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:7}}>Substitutes</div>
        <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
          {subsList.map(pid=>{const p=players.find(pl=>pl.id===pid);if(!p)return null;return(
            <div key={pid} style={{display:"flex",alignItems:"center",gap:5,background:"var(--c-bg0)",borderRadius:6,padding:"4px 9px",border:"1px solid var(--c-border)"}}>
              <span style={{fontSize:9,fontWeight:700,color:"var(--c-muted)"}}>#{p.number}</span>
              <span style={{fontSize:10,fontWeight:600}}>{p.name.split(" ")[0]}</span>
            </div>
          );})}
        </div>
      </div>
    )}
  </div>);
}

// ── MatchModal ────────────────────────────────────────────────────────────────

export function MatchModal({match,players,tc,token,teamId,orgId,coachId,lineups,strategies,onClose,showCloseWarn=false,onCancelCloseWarn,onConfirmClose,onSave,onAwarded,readOnly,featAchievements=true,featPlayerDev=true,featPlayerRatings=true,featMatchDay=false,onMatchDayUpdate,onMatchDayFinished}){
  // Match Day is deliberately "hard to close" once it's actually running —
  // nothing is ever lost (every action saves straight to the database as it
  // happens), but closing mid-match would leave the coach unsure whether
  // they're still tracking it. onClose (the × and tapping outside) and the
  // hardware/browser back button both route through the parent's guard,
  // which shows this warning instead of closing while matchDayActive.
  const matchDayActive=featMatchDay&&!!(match.match_day&&["running","paused","break"].includes(match.match_day.status));
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<640);
  useEffect(()=>{const h=()=>setIsMobile(window.innerWidth<640);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  const [tab,setTab]=useState(match.played?"result":"lineup");
  useEffect(()=>{ if((tab==="development"&&!featPlayerDev)||(tab==="awards"&&!featAchievements)||(tab==="ratings"&&!featPlayerRatings))setTab("result"); },[tab,featPlayerDev,featAchievements,featPlayerRatings]);
  // Match Day gets a true full-screen layout on mobile — edge to edge, no
  // rounded bottom-sheet corners — so the live pitch/timer view has room.
  const fullScreenMD=isMobile&&tab==="matchday"&&featMatchDay;
  const [sc,setSc]=useState(match.sideCount||11);const [sl,setSl]=useState(()=>match.slots||mkSlots(match.sideCount||11));
  const [subs,setSubs]=useState(match.subs||[]);
  const [showStrategies,setShowStrategies]=useState(false);
  const [showLineups,setShowLineups]=useState(false);
  const [showRoles,setShowRoles]=useState(false);
  const [sidePanelTab,setSidePanelTab]=useState("lineups"); // "lineups" | "strategies" — desktop side panel only
  const [openDevIds,setOpenDevIds]=useState([]);
  const [reviews,setReviews]=useState([]);
  const [showReviews,setShowReviews]=useState(false);
  useEffect(()=>{
    api.get(token,"match_reviews",`fixture_id=eq.${match.id}`).then(rows=>setReviews(rows||[]));
  },[match.id]);
  const reviewSquadIds=[...new Set([...(match.slots||[]).map(s=>s.playerId).filter(Boolean),...(match.subs||[])])];
  const [strategyDrawings,setStrategyDrawings]=useState([]);
  const [previewSource,setPreviewSource]=useState(null); // {type:"lineup"|"strategy", id} — whichever is currently being previewed, if any
  const preSavedLineupRef=useRef(null); // snapshot of {sc,sl,subs} from just before the first preview started, so Hide always restores the true original
  function showLineupPreview(l){
    if(!preSavedLineupRef.current)preSavedLineupRef.current={sc,sl,subs};
    setSc(l.side_count||11);setSl(l.slots||mkSlots(l.side_count||11));setSubs(l.subs||[]);
    setPreviewSource({type:"lineup",id:l.id});
  }
  function showStrategyPreview(s){
    if(!preSavedLineupRef.current)preSavedLineupRef.current={sc,sl,subs};
    setSc(s.side_count||11);setSl(s.slots||mkSlots(s.side_count||11));setSubs(s.subs||[]);setStrategyDrawings(s.drawings||[]);
    setPreviewSource({type:"strategy",id:s.id});
  }
  function hidePreview(){
    if(preSavedLineupRef.current){
      setSc(preSavedLineupRef.current.sc);setSl(preSavedLineupRef.current.sl);setSubs(preSavedLineupRef.current.subs);
      preSavedLineupRef.current=null;
    }
    setStrategyDrawings([]);
    setPreviewSource(null);
  }

  const [attachedStrategyIds,setAttachedStrategyIds]=useState(match.attached_strategy_ids||[]);
  const [captainId,setCaptainId]=useState(match.captain_id||"");
  const [cornerLeftId,setCornerLeftId]=useState(match.corner_taker_left_id||"");
  const [cornerRightId,setCornerRightId]=useState(match.corner_taker_right_id||"");
  const [freeKickId,setFreeKickId]=useState(match.free_kick_taker_id||"");
  const [penaltyId,setPenaltyId]=useState(match.penalty_taker_id||"");
  const [our,setOur]=useState(match.ourScore??0);const [opp,setOpp]=useState(match.oppScore??0);
  const [venueType,setVenueType]=useState(match.venue_type||"home");
  // Times are editable here as well as on the "Add Fixture" form — this modal is the
  // only place a coach can revisit a fixture, so without these a meeting time could
  // only ever be set at creation and never corrected.
  const [meetingTime,setMeetingTime]=useState(match.meeting_time||"");
  const [kickoffTime,setKickoffTime]=useState(match.kickoff||"");
  const [scorers,setScorers]=useState(match.scorers||[]);const [assists,setAssists]=useState(match.assisters||[]);
  const [played,setPlayed]=useState(match.played||false);const [notes,setNotes]=useState(match.notes||"");
  // Match Day already committed this fixture's result (played/score/events,
  // plus season stats/achievements/notifications) end-to-end — so the Result
  // tab defaults to a locked, read-only summary instead of re-presenting
  // editable controls that would imply Save Match still needs clicking.
  // "Edit" unlocks it for coaches who need to fix a mistake.
  const resultFromMatchDay=!!(match.match_day&&match.match_day.status==="finished");
  const [resultUnlocked,setResultUnlocked]=useState(false);
  const resultLocked=resultFromMatchDay&&!resultUnlocked;
  const [playerNotes,setPlayerNotes]=useState({}); // {playerId: {id, note}}
  const [editingPid,setEditingPid]=useState(null);
  const [editText,setEditText]=useState("");
  const [ratings,setRatings]=useState({}); // {playerId: {player:{id,rating}|null, coach:{id,rating}|null}}
  const [matchTeamAch,setMatchTeamAch]=useState([]);
  const [matchPlayerAch,setMatchPlayerAch]=useState([]);
  const [awardPick,setAwardPick]=useState(null); // {scope,targetId,targetName}
  useEffect(()=>{ loadPlayerNotes(); loadRatings(); loadMatchAchievements(); },[]);
  async function loadMatchAchievements(){
    if(!token||!match.id)return;
    const [tRows,pRows]=await Promise.all([
      api.get(token,"team_achievements",`fixture_id=eq.${match.id}`),
      api.get(token,"player_achievements",`fixture_id=eq.${match.id}`),
    ]);
    setMatchTeamAch(tRows||[]);
    setMatchPlayerAch(pRows||[]);
  }
  async function awardInMatch(key){
    if(!awardPick)return;
    if(awardPick.scope==="team"){
      const created=await api.post(token,"team_achievements",[{team_id:teamId,achievement_key:key,fixture_id:match.id,awarded_by:coachId}]);
      if(created&&created[0]){setMatchTeamAch(prev=>[...prev,created[0]]);if(onAwarded)onAwarded("team",teamId);}
    } else {
      const created=await api.post(token,"player_achievements",[{player_id:awardPick.targetId,achievement_key:key,fixture_id:match.id,awarded_by:coachId}]);
      if(created&&created[0]){setMatchPlayerAch(prev=>[...prev,created[0]]);if(onAwarded)onAwarded("individual",awardPick.targetId);}
    }
    setAwardPick(null);
  }
  async function loadRatings(){
    if(!token||!match.id)return;
    const rows=await api.get(token,"match_ratings",`fixture_id=eq.${match.id}`);
    const map={};
    (rows||[]).forEach(r=>{
      if(!map[r.player_id])map[r.player_id]={player:null,coach:null};
      map[r.player_id][r.source]={id:r.id,rating:r.rating};
    });
    setRatings(map);
  }
  async function setCoachRating(pid,rating){
    const existing=ratings[pid]?.coach;
    if(existing){
      await api.patch(token,"match_ratings",existing.id,{rating,updated_at:new Date().toISOString()});
      setRatings(prev=>({...prev,[pid]:{...prev[pid],coach:{id:existing.id,rating}}}));
    } else {
      const created=await api.post(token,"match_ratings",[{fixture_id:match.id,player_id:pid,source:"coach",rating}]);
      if(created&&created[0])setRatings(prev=>({...prev,[pid]:{player:prev[pid]?.player||null,coach:{id:created[0].id,rating}}}));
    }
  }
  async function loadPlayerNotes(){
    if(!token||!match.id)return;
    const rows=await api.get(token,"fixture_player_notes",`fixture_id=eq.${match.id}`);
    const map={};
    (rows||[]).forEach(r=>{map[r.player_id]={id:r.id,note:r.note};});
    setPlayerNotes(map);
  }
  function startEditPlayerNote(pid){ setEditingPid(pid); setEditText(playerNotes[pid]?.note||""); }
  async function savePlayerNote(pid){
    const text=editText.trim();
    const existing=playerNotes[pid];
    if(!text){
      if(existing)await api.del(token,"fixture_player_notes",existing.id);
      setPlayerNotes(prev=>{const n={...prev};delete n[pid];return n;});
    } else if(existing){
      await api.patch(token,"fixture_player_notes",existing.id,{note:text,updated_at:new Date().toISOString()});
      setPlayerNotes(prev=>({...prev,[pid]:{id:existing.id,note:text}}));
    } else {
      const created=await api.post(token,"fixture_player_notes",[{fixture_id:match.id,player_id:pid,note:text}]);
      if(created&&created[0])setPlayerNotes(prev=>({...prev,[pid]:{id:created[0].id,note:text}}));
    }
    setEditingPid(null);
  }
  function addG(pid){setScorers(p=>{const e=p.find(s=>s.playerId===pid);return e?p.map(s=>s.playerId===pid?{...s,count:s.count+1}:s):[...p,{playerId:pid,count:1}];});setOur(s=>s+1);setPlayed(true);}
  function remG(pid){setScorers(p=>{const e=p.find(s=>s.playerId===pid);if(!e||e.count<=0)return p;const nc=e.count-1;setOur(s=>Math.max(0,s-1));return nc===0?p.filter(s=>s.playerId!==pid):p.map(s=>s.playerId===pid?{...s,count:nc}:s);});}
  function addA(pid){setAssists(p=>{const e=p.find(s=>s.playerId===pid);return e?p.map(s=>s.playerId===pid?{...s,count:s.count+1}:s):[...p,{playerId:pid,count:1}];});}
  function remA(pid){setAssists(p=>{const e=p.find(s=>s.playerId===pid);if(!e||e.count<=0)return p;const nc=e.count-1;return nc===0?p.filter(s=>s.playerId!==pid):p.map(s=>s.playerId===pid?{...s,count:nc}:s);});}
  const inp={padding:"7px 9px",borderRadius:6,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:12,outline:"none",boxSizing:"border-box"};
  const tb=(t,l)=><button onClick={()=>setTab(t)} style={{padding:"5px 11px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:tab===t?tc:"transparent",color:tab===t?"#fff":"var(--c-muted)"}}>{l}</button>;
  return(<>
  {/* Match Day full-screen (mobile) covers the app's bottom nav entirely (zIndex
      above it, bottom:0 not offset by nav-h) rather than sitting above it — that
      way its own Save Match footer is never hidden behind the nav bar, and there's
      no confusing sliver of the app's normal navigation visible mid-match. */}
  <div onClick={onClose} style={{position:"fixed",inset:0,bottom:fullScreenMD?0:(isMobile?"var(--nav-h,65px)":0),background:fullScreenMD?"var(--c-surface)":"rgba(0,0,0,0.7)",display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",zIndex:fullScreenMD?250:100,padding:isMobile?0:16}}>
    <div onClick={e=>e.stopPropagation()} style={{background:"var(--c-surface)",borderRadius:fullScreenMD?0:(isMobile?"16px 16px 0 0":14),width:"100%",maxWidth:isMobile?"100%":760,height:fullScreenMD?"100%":undefined,maxHeight:fullScreenMD?"100%":(isMobile?"90vh":"85vh"),border:fullScreenMD?"none":"1px solid var(--c-border)",display:"flex",flexDirection:"column",overscrollBehavior:"contain"}}>
      {matchDayActive&&(
        <div style={{background:"#f59e0b22",borderBottom:"1px solid #f59e0b55",padding:"6px 18px",display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
          <span style={{fontSize:12}}>🔴</span>
          <span style={{fontSize:10,fontWeight:700,color:"#b45309"}}>Match Day in progress — everything's saving automatically</span>
        </div>
      )}
      <div style={{padding:"15px 18px 12px",borderBottom:"1px solid var(--c-border)",flexShrink:0}}>
        {/* The opponent name is the modal's title and gets its own band, separated
            from the date/time details by a rule that runs the full width of the
            header. Previously the title, the date, both time pickers and the
            review button were all stacked inside one column beside the close
            button, which read as an undifferentiated block — nothing told you
            where the heading ended and the editable detail began. Keeping the
            rule outside the title/close flex row is what lets it span the whole
            modal rather than stopping at the width of the text. */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:16,fontWeight:800,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              vs {match.opponent||"TBC"}
              {!readOnly?(
                <span style={{display:"flex",gap:3,background:"var(--c-bg0)",borderRadius:6,padding:2}}>
                  {[["home","🏠"],["away","✈️"]].map(([v,ic])=>(
                    <button key={v} title={v==="home"?"Home":"Away"} onClick={()=>setVenueType(v)} style={{padding:"2px 8px",borderRadius:5,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:venueType===v?tc:"transparent",color:venueType===v?"#fff":"var(--c-muted)"}}>{ic}</button>
                  ))}
                </span>
              ):(
                <span style={{fontSize:10,fontWeight:700,color:"var(--c-muted)",background:"var(--c-bg0)",borderRadius:5,padding:"3px 8px"}}>{venueType==="away"?"✈️ Away":"🏠 Home"}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} title={matchDayActive?"Match in progress — tap for options":"Close"} style={{background:matchDayActive?"#f59e0b22":"none",border:matchDayActive?"1px solid #f59e0b55":"none",borderRadius:matchDayActive?7:0,padding:matchDayActive?"3px 9px":0,color:matchDayActive?"#b45309":"var(--c-muted)",cursor:"pointer",fontSize:18,lineHeight:1,fontWeight:matchDayActive?700:400,flexShrink:0}}>×</button>
        </div>

        <div style={{height:1,background:"var(--c-border)",margin:"11px 0 13px"}}/>

        {readOnly?(
              <MatchKeyInfo match={{...match,meeting_time:meetingTime,kickoff:kickoffTime}} ac={tc} compact/>
            ):(
              <div style={{display:"flex",gap:14,alignItems:"flex-end",flexWrap:"wrap",rowGap:10}}>
                <div style={{fontSize:11,color:"var(--c-muted)",paddingBottom:6}}>{fmtDate(match.date)}{match.venue?` · ${match.venue}`:""}</div>
                <div>
                  <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.4px",textTransform:"uppercase",color:"var(--c-muted)",marginBottom:2}}>🕒 Meeting Time</div>
                  <input type="time" value={meetingTime} onChange={e=>setMeetingTime(e.target.value)} style={{padding:"5px 7px",borderRadius:6,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:12,fontWeight:700}}/>
                </div>
                <div>
                  <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.4px",textTransform:"uppercase",color:"var(--c-muted)",marginBottom:2}}>⚽ Kick Off</div>
                  <input type="time" value={kickoffTime} onChange={e=>setKickoffTime(e.target.value)} style={{padding:"5px 7px",borderRadius:6,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:12,fontWeight:700}}/>
                </div>
                {match.map_url&&<a href={match.map_url} target="_blank" rel="noopener noreferrer" style={{color:tc,fontWeight:700,fontSize:11,textDecoration:"none",paddingBottom:6}}>📍 View on Map</a>}
              </div>
            )}
        {reviewSquadIds.length>0&&(
              <button onClick={()=>setShowReviews(s=>!s)} style={{marginTop:11,display:"flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:7,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontWeight:700,fontSize:11,cursor:"pointer"}}>✓ Reviewed {reviews.length}/{reviewSquadIds.length}</button>
            )}
        {showReviews&&reviewSquadIds.length>0&&(
          <div style={{marginTop:9,background:"var(--c-bg0)",borderRadius:8,padding:"8px 10px",display:"flex",flexDirection:"column",gap:5}}>
            {reviewSquadIds.map(pid=>{
              const p=players.find(pl=>pl.id===pid);
              if(!p)return null;
              const has=reviews.some(r=>r.player_id===pid);
              return(
                <div key={pid} style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:16,height:16,borderRadius:"50%",background:has?"#22c55e":"var(--c-border)",color:"#fff",fontSize:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{has?"✓":""}</span>
                  <span style={{fontSize:12,color:has?"var(--c-text)":"var(--c-muted)"}}>{p.name}</span>
                </div>
              );
            })}
          </div>
        )}
        <div style={{display:"flex",gap:3,marginTop:13,background:"var(--c-bg0)",borderRadius:7,padding:3,flexWrap:"wrap"}}>{tb("lineup","🗂 Lineup")}{featMatchDay&&tb("matchday","🏟 Match Day")}{tb("result","📊 Result")}{played&&featPlayerRatings&&tb("ratings","⭐ Ratings")}{played&&featPlayerDev&&tb("development","📈 Development")}{played&&featAchievements&&tb("awards","🏅 Awards")}{tb("notes","📝 Notes")}</div>
      </div>
      <div style={{padding:16,flex:1,overflowY:"auto",minHeight:0,overscrollBehavior:"contain",WebkitOverflowScrolling:"touch"}}>
        {tab==="lineup"&&(()=>{
          const lineupsPanelContent=(
            <div style={{background:"var(--c-bg0)",borderRadius:9,padding:12}}>
              <div style={{fontSize:10,color:"var(--c-muted2)",marginBottom:9}}>Preview a saved starting team's formation and subs on this match — hide it any time to go back to what was there before.</div>
              {(!lineups||lineups.length===0)?(
                <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>No starting teams saved yet — save one from the Team Planner page.</div>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {lineups.map(l=>{
                    const isShowing=previewSource?.type==="lineup"&&previewSource.id===l.id;
                    return(
                      <div key={l.id} style={{display:"flex",alignItems:"center",gap:8,background:isShowing?tc+"22":"var(--c-surface)",border:isShowing?`1px solid ${tc}`:"1px solid transparent",borderRadius:7,padding:"7px 10px"}}>
                        <span style={{flex:1,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</span>
                        <button onClick={()=>isShowing?hidePreview():showLineupPreview(l)} style={{fontSize:10,fontWeight:700,color:isShowing?"#ef4444":tc,background:"none",border:"none",cursor:"pointer"}}>{isShowing?"Hide":"Show"}</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
          const strategiesPanelContent=(
            <div style={{background:"var(--c-bg0)",borderRadius:9,padding:12}}>
              <div style={{fontSize:10,color:"var(--c-muted2)",marginBottom:9}}>Preview a saved strategy's formation, subs, and drawings on this match — hide it any time to go back to what was there before. Tick "Show to players" separately to let them view it, without changing the lineup.</div>
              {(!strategies||strategies.length===0)?(
                <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>No strategies saved yet — save one from the Team Planner page.</div>
              ):(["attack","defence","general","setpieces"].map(cat=>{
                const list=strategies.filter(s=>s.category===cat);
                if(list.length===0)return null;
                return(
                  <div key={cat} style={{marginBottom:10}}>
                    <div style={{fontSize:9,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{cat==="attack"?"⚔️ Attack":cat==="defence"?"🛡️ Defence":cat==="setpieces"?"🥅 Set Pieces":"📋 General"}</div>
                    <div style={{display:"flex",flexDirection:"column",gap:5}}>
                      {list.map(s=>{
                        const isAttached=attachedStrategyIds.includes(s.id);
                        const isShowing=previewSource?.type==="strategy"&&previewSource.id===s.id;
                        return(
                          <div key={s.id} style={{background:isShowing?tc+"22":"var(--c-surface)",border:isShowing?`1px solid ${tc}`:"1px solid transparent",borderRadius:7,padding:"7px 10px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <span style={{flex:1,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</span>
                              <button onClick={()=>isShowing?hidePreview():showStrategyPreview(s)} style={{fontSize:10,fontWeight:700,color:isShowing?"#ef4444":tc,background:"none",border:"none",cursor:"pointer"}}>{isShowing?"Hide":"Show"}</button>
                            </div>
                            {s.notes&&<div style={{fontSize:10,color:"var(--c-muted)",marginTop:4,lineHeight:1.4}}>{s.notes}</div>}
                            <label style={{display:"flex",alignItems:"center",gap:6,marginTop:6,cursor:"pointer"}}>
                              <input type="checkbox" checked={isAttached} onChange={()=>setAttachedStrategyIds(prev=>isAttached?prev.filter(id=>id!==s.id):[...prev,s.id])}/>
                              <span style={{fontSize:10,color:isAttached?tc:"var(--c-muted)",fontWeight:isAttached?700:500}}>👁 Show to players for this match</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }))}
            </div>
          );
          const rolesPanelContent=(
            <div style={{background:"var(--c-bg0)",borderRadius:9,padding:12}}>
              <div style={{fontSize:10,color:"var(--c-muted2)",marginBottom:10}}>Assign match-day roles — visible to players once saved.</div>
              {[["Captain",captainId,setCaptainId],["Corner Taker (Left)",cornerLeftId,setCornerLeftId],["Corner Taker (Right)",cornerRightId,setCornerRightId],["Free Kick Taker",freeKickId,setFreeKickId],["Penalty Taker",penaltyId,setPenaltyId]].map(([label,val,setter])=>(
                <div key={label} style={{marginBottom:10}}>
                  <label style={{display:"block",fontSize:10,fontWeight:700,color:"var(--c-muted)",marginBottom:4}}>{label}</label>
                  <select value={val} onChange={e=>setter(e.target.value)} disabled={readOnly} style={{width:"100%",padding:"7px 8px",borderRadius:6,border:"1px solid var(--c-border)",background:"var(--c-surface)",color:"var(--c-text)",fontSize:12,cursor:readOnly?"default":"pointer"}}>
                    <option value="">— None —</option>
                    {players.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          );
          const pitchEl=<Pitch players={players} slots={sl} setSlots={setSl} tc={tc} sideCount={sc} setSideCount={setSc} subs={subs} setSubs={readOnly?null:setSubs} readOnly={readOnly} drawings={strategyDrawings.length>0?strategyDrawings:undefined}/>;

          if(isMobile){
            return(<>
              {!readOnly&&(
                <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:9,flexWrap:"wrap"}}>
                  <button onClick={()=>{setShowLineups(s=>!s);setShowStrategies(false);setShowRoles(false);}} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${tc}`,background:showLineups?tc:"none",color:showLineups?"#fff":tc,fontWeight:700,fontSize:11,cursor:"pointer"}}>🏁 Starting Teams</button>
                  <button onClick={()=>{setShowStrategies(s=>!s);setShowLineups(false);setShowRoles(false);}} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${tc}`,background:showStrategies?tc:"none",color:showStrategies?"#fff":tc,fontWeight:700,fontSize:11,cursor:"pointer"}}>🎯 Strategies</button>
                  <button onClick={()=>{setShowRoles(s=>!s);setShowLineups(false);setShowStrategies(false);}} style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${tc}`,background:showRoles?tc:"none",color:showRoles?"#fff":tc,fontWeight:700,fontSize:11,cursor:"pointer"}}>👑 Roles</button>
                </div>
              )}
              {showLineups&&<div style={{marginBottom:12}}>{lineupsPanelContent}</div>}
              {showStrategies&&<div style={{marginBottom:12}}>{strategiesPanelContent}</div>}
              {showRoles&&<div style={{marginBottom:12}}>{rolesPanelContent}</div>}
              {pitchEl}
            </>);
          }
          return(
            <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
              <div style={{flex:1,minWidth:0}}>{pitchEl}</div>
              {!readOnly&&(
                <div style={{width:230,flexShrink:0}}>
                  <div style={{display:"flex",gap:3,marginBottom:10,background:"var(--c-bg0)",borderRadius:7,padding:3}}>
                    <button onClick={()=>setSidePanelTab("lineups")} style={{flex:1,padding:"6px 2px",borderRadius:5,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,background:sidePanelTab==="lineups"?tc:"transparent",color:sidePanelTab==="lineups"?"#fff":"var(--c-muted)"}}>🏁 Teams</button>
                    <button onClick={()=>setSidePanelTab("strategies")} style={{flex:1,padding:"6px 2px",borderRadius:5,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,background:sidePanelTab==="strategies"?tc:"transparent",color:sidePanelTab==="strategies"?"#fff":"var(--c-muted)"}}>🎯 Strategies</button>
                    <button onClick={()=>setSidePanelTab("roles")} style={{flex:1,padding:"6px 2px",borderRadius:5,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,background:sidePanelTab==="roles"?tc:"transparent",color:sidePanelTab==="roles"?"#fff":"var(--c-muted)"}}>👑 Roles</button>
                  </div>
                  {sidePanelTab==="lineups"?lineupsPanelContent:sidePanelTab==="strategies"?strategiesPanelContent:rolesPanelContent}
                </div>
              )}
            </div>
          );
        })()}
        {tab==="matchday"&&featMatchDay&&(
          <MatchDayPanel match={{...match,slots:sl,subs}} players={players} tc={tc} token={token} teamId={teamId} orgId={orgId} readOnly={readOnly} onMatchDayUpdate={onMatchDayUpdate} onMatchDayFinished={onMatchDayFinished} onResultCarryOver={({ourScore,oppScore,scorers,assisters})=>{setPlayed(true);setOur(ourScore);setOpp(oppScore);setScorers(scorers);setAssists(assisters);setTab("result");}}/>
        )}
        {tab==="result"&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          {resultLocked?(<>
            <div style={{background:"#22c55e22",border:"1px solid #22c55e55",borderRadius:9,padding:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span style={{fontSize:18}}>✓</span>
              <span style={{flex:1,fontSize:12,color:"#16a34a",fontWeight:600,minWidth:180}}>Result saved automatically via Match Day — season stats, achievements and notifications are already recorded. Nothing else to save here.</span>
              {!readOnly&&<button onClick={()=>setResultUnlocked(true)} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #16a34a",background:"none",color:"#16a34a",fontWeight:700,fontSize:11,cursor:"pointer"}}>✎ Edit</button>}
            </div>
            <div style={{background:"var(--c-bg0)",borderRadius:10,padding:13,textAlign:"center"}}>
              <div style={{fontSize:9,color:"var(--c-text)",marginBottom:7,fontWeight:600,textTransform:"uppercase"}}>Final Score</div>
              <div style={{fontSize:32,fontWeight:900,color:tc}}>{our} – {opp}</div>
              <div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>Us vs {match.opponent||"Opp"}</div>
              <div style={{marginTop:7,fontSize:11,fontWeight:700,color:our>opp?"#10b981":our<opp?"#ef4444":"#f59e0b"}}>{our>opp?"WIN ✓":our<opp?"LOSS ✗":"DRAW ═"}</div>
            </div>
            {[["⚽ Goal Scorers",scorers,tc],["🎯 Assisters",assists,"#8b5cf6"]].map(([title,list,col])=>list.filter(s=>s.count>0).length>0&&(
              <div key={title}>
                <div style={{fontSize:9,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{title}</div>
                {list.filter(s=>s.count>0).map(s=>{const p=players.find(pl=>pl.id===s.playerId);if(!p)return null;return(
                  <div key={s.playerId} style={{display:"flex",alignItems:"center",gap:7,padding:"5px 9px",background:"var(--c-bg0)",borderRadius:6,marginBottom:3}}>
                    <span style={{fontSize:11,fontWeight:600,flex:1}}><Flag value={p.nationality}/> {p.name}</span>
                    <span style={{fontSize:12,fontWeight:800,color:col}}>{s.count}</span>
                  </div>
                );})}
              </div>
            ))}
          </>):(<>
          <div style={{display:"flex",alignItems:"center",gap:7}}><label style={{fontSize:10,fontWeight:600,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Played?</label><button onClick={()=>setPlayed(p=>{const next=!p;if(!next&&["ratings","development","awards"].includes(tab))setTab("result");return next;})} style={{padding:"4px 12px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:played?"#10b981":"var(--c-border)",color:"#fff"}}>{played?"Yes":"No"}</button>
            {resultFromMatchDay&&<span style={{fontSize:10,color:"var(--c-muted2)",fontStyle:"italic"}}>Editing the Match Day result — Save Match will update the recorded stats/achievements too.</span>}
          </div>
          {played&&<><div style={{background:"var(--c-bg0)",borderRadius:10,padding:13,textAlign:"center"}}>
            <div style={{fontSize:9,color:"var(--c-text)",marginBottom:7,fontWeight:600,textTransform:"uppercase"}}>Final Score</div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:14}}>
              {[[our,setOur,tc,"Us"],[opp,setOpp,"var(--c-text2)",match.opponent||"Opp"]].map(([v,set,col,lbl],i)=><div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <span style={{fontSize:9,color:"var(--c-text2)"}}>{lbl}</span>
                <div style={{display:"flex",alignItems:"center",gap:4}}><button onClick={()=>set(s=>Math.max(0,s-1))} style={{width:24,height:24,borderRadius:4,border:"1px solid var(--c-border)",background:"var(--c-surface)",color:"var(--c-text)",cursor:"pointer",fontSize:13,lineHeight:1}}>−</button><span style={{fontSize:32,fontWeight:900,color:col,minWidth:36,textAlign:"center"}}>{v}</span><button onClick={()=>set(s=>s+1)} style={{width:24,height:24,borderRadius:4,border:"1px solid var(--c-border)",background:"var(--c-surface)",color:"var(--c-text)",cursor:"pointer",fontSize:13,lineHeight:1}}>+</button></div>
              </div>)}
            </div>
            <div style={{marginTop:7,fontSize:11,fontWeight:700,color:our>opp?"#10b981":our<opp?"#ef4444":"#f59e0b"}}>{our>opp?"WIN ✓":our<opp?"LOSS ✗":"DRAW ═"}</div>
          </div>
          {[["⚽ Goal Scorers",scorers,addG,remG,tc],["🎯 Assisters",assists,addA,remA,"#8b5cf6"]].map(([title,list,add,rem,col])=><div key={title}>
            <div style={{fontSize:9,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{title}</div>
            {players.map(p=>{const s=list.find(s=>s.playerId===p.id);const cnt=s?.count||0;return(<div key={p.id} style={{display:"flex",alignItems:"center",gap:7,padding:"5px 9px",background:"var(--c-bg0)",borderRadius:6,marginBottom:3}}>
              <span style={{fontSize:11,fontWeight:600,flex:1}}><Flag value={p.nationality}/> {p.name}</span>
              <button onClick={()=>rem(p.id)} style={{width:20,height:20,borderRadius:4,border:"1px solid var(--c-border)",background:"var(--c-surface)",color:"var(--c-text2)",cursor:"pointer",fontSize:11,lineHeight:1}}>−</button>
              <span style={{fontSize:12,fontWeight:800,minWidth:16,textAlign:"center",color:cnt>0?col:"var(--c-muted2)"}}>{cnt}</span>
              <button onClick={()=>add(p.id)} style={{width:20,height:20,borderRadius:4,border:"1px solid var(--c-border)",background:"var(--c-surface)",color:"var(--c-text2)",cursor:"pointer",fontSize:11,lineHeight:1}}>+</button>
            </div>);})}
          </div>)}</>}
          </>)}
        </div>}
        {tab==="ratings"&&featPlayerRatings&&<div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:11,color:"var(--c-muted2)",marginBottom:4}}>Rate each player's performance out of 10. Players can also rate their own performance — you'll see it here once they do.</div>
          {players.map(p=>{
            const r=ratings[p.id]||{player:null,coach:null};
            return(
              <div key={p.id} style={{background:"var(--c-bg0)",borderRadius:8,padding:"9px 11px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <span style={{fontSize:12,fontWeight:600,flex:1}}><Flag value={p.nationality}/> {p.name}</span>
                  <span style={{fontSize:10,color:"var(--c-muted)"}}>Player's own rating: <b style={{color:r.player?tc:"var(--c-muted2)"}}>{r.player?`${r.player.rating}/10`:"—"}</b></span>
                </div>
                {!readOnly&&<div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                  {[1,2,3,4,5,6,7,8,9,10].map(n=>(
                    <button key={n} onClick={()=>setCoachRating(p.id,n)} style={{width:24,height:24,borderRadius:5,border:"none",cursor:"pointer",fontSize:10,fontWeight:700,background:r.coach?.rating===n?tc:"var(--c-surface)",color:r.coach?.rating===n?"#fff":"var(--c-muted)"}}>{n}</button>
                  ))}
                </div>}
                {readOnly&&<div style={{fontSize:10,color:"var(--c-muted)"}}>Your rating: {r.coach?`${r.coach.rating}/10`:"Not rated"}</div>}
              </div>
            );
          })}
        </div>}
        {tab==="development"&&featPlayerDev&&<div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:11,color:"var(--c-muted2)",marginBottom:4}}>Nudge each player's strengths/development areas by up to ±1 based on this match. Players can do the same for their own — the running total combines both.</div>
          {players.map(p=>{
            const isOpen=openDevIds.includes(p.id);
            return(
              <div key={p.id} style={{background:"var(--c-bg0)",borderRadius:8,padding:"9px 11px"}}>
                <button onClick={()=>setOpenDevIds(prev=>prev.includes(p.id)?prev.filter(id=>id!==p.id):[...prev,p.id])} style={{width:"100%",display:"flex",alignItems:"center",gap:8,background:"none",border:"none",padding:0,cursor:"pointer",textAlign:"left"}}>
                  <span style={{fontSize:10,color:"var(--c-muted)",transform:isOpen?"rotate(90deg)":"none",transition:"transform 0.15s",flexShrink:0}}>▶</span>
                  <span style={{fontSize:12,fontWeight:700,flex:1}}><Flag value={p.nationality}/> {p.name}</span>
                </button>
                {isOpen&&(
                  <div style={{marginTop:9}}>
                    {readOnly?(
                      <div style={{fontSize:10,color:"var(--c-muted2)",fontStyle:"italic"}}>View-only</div>
                    ):(
                      <DevPointsPanel playerId={p.id} fixtureId={match.id} source="coach" token={token} ac={tc} AS={{surfaceColor:"var(--c-surface)",borderColor:"var(--c-border)",bgColor:"var(--c-bg0)"}}/>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>}
        {tab==="awards"&&featAchievements&&<div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Team Achievements (this match)</div>
              {!readOnly&&<button onClick={()=>setAwardPick({scope:"team"})} style={{fontSize:10,fontWeight:700,color:tc,background:"none",border:"none",cursor:"pointer"}}>+ Award</button>}
            </div>
            <AchievementBadges rows={matchTeamAch} emptyText="No team achievements awarded for this match."/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Individual Achievements (this match)</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {players.map(p=>{
                const rows=matchPlayerAch.filter(r=>r.player_id===p.id);
                return(
                  <div key={p.id} style={{background:"var(--c-bg0)",borderRadius:8,padding:"9px 11px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:rows.length>0?8:0}}>
                      <span style={{fontSize:12,fontWeight:600,flex:1}}><Flag value={p.nationality}/> {p.name}</span>
                      {!readOnly&&<button onClick={()=>setAwardPick({scope:"individual",targetId:p.id,targetName:p.name})} style={{fontSize:10,fontWeight:700,color:tc,background:"none",border:"none",cursor:"pointer"}}>+ Award</button>}
                    </div>
                    {rows.length>0&&<AchievementBadges rows={rows}/>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>}
        {tab==="notes"&&<div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div><label style={{display:"block",fontSize:10,fontWeight:600,color:"var(--c-text)",marginBottom:5,textTransform:"uppercase"}}>Match Notes (whole team)</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Tactics, game plan, things everyone should know…" rows={5} style={{...inp,width:"100%",resize:"vertical",fontFamily:"inherit",lineHeight:1.6}}/></div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:600,color:"var(--c-text)",marginBottom:5,textTransform:"uppercase"}}>Individual Player Notes</label>
            <div style={{fontSize:10,color:"var(--c-muted2)",marginBottom:8}}>Visible to the whole team, not just the named player.</div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              {players.map(p=>{
                const existing=playerNotes[p.id];
                const isEditing=editingPid===p.id;
                return(
                  <div key={p.id} style={{background:"var(--c-bg0)",borderRadius:8,padding:"8px 10px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:12,fontWeight:600,flex:1}}><Flag value={p.nationality}/> {p.name}</span>
                      {!readOnly&&!isEditing&&<button onClick={()=>startEditPlayerNote(p.id)} style={{padding:"3px 9px",borderRadius:5,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontSize:10,cursor:"pointer"}}>{existing?"Edit":"+ Add note"}</button>}
                    </div>
                    {!isEditing&&existing&&<div style={{fontSize:11,color:"var(--c-text2)",marginTop:5,whiteSpace:"pre-wrap"}}>{existing.note}</div>}
                    {isEditing&&<div style={{marginTop:7,display:"flex",flexDirection:"column",gap:6}}>
                      <textarea value={editText} onChange={e=>setEditText(e.target.value)} placeholder="e.g. work on first touch, great tracking back…" rows={2} style={{...inp,width:"100%",resize:"vertical",fontFamily:"inherit"}}/>
                      <div style={{display:"flex",gap:6}}>
                        <button onClick={()=>savePlayerNote(p.id)} style={{padding:"5px 12px",borderRadius:6,border:"none",background:tc,color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Save</button>
                        <button onClick={()=>setEditingPid(null)} style={{padding:"5px 12px",borderRadius:6,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer"}}>Cancel</button>
                      </div>
                    </div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>}
      </div>
      {!readOnly&&<div style={{padding:"12px 16px",paddingBottom:"calc(12px + env(safe-area-inset-bottom, 0px))",borderTop:"1px solid var(--c-border)",flexShrink:0,background:"var(--c-surface)"}}><button onClick={()=>{onSave({...match,slots:sl,sideCount:sc,subs,ourScore:our,oppScore:opp,venue_type:venueType,meeting_time:meetingTime||null,kickoff:kickoffTime||null,scorers,assisters:assists,played,notes,attached_strategy_ids:attachedStrategyIds,captain_id:captainId||null,corner_taker_left_id:cornerLeftId||null,corner_taker_right_id:cornerRightId||null,free_kick_taker_id:freeKickId||null,penalty_taker_id:penaltyId||null});onClose();}} style={{width:"100%",padding:"12px",borderRadius:9,border:"none",background:tc,color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer"}}>Save Match ✓</button></div>}
    </div>
  </div>
  {awardPick&&(
    <AwardAchievementModal scope={awardPick.scope} targetName={awardPick.scope==="team"?"the team":awardPick.targetName} onClose={()=>setAwardPick(null)} onAward={awardInMatch} ac={tc} AS={{surfaceColor:"var(--c-surface)",borderColor:"var(--c-border)",bgColor:"var(--c-bg0)"}} isMobile={isMobile}/>
  )}
  {showCloseWarn&&(
    <div onClick={onCancelCloseWarn} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--c-surface)",borderRadius:14,border:"1px solid var(--c-border)",width:"100%",maxWidth:360,padding:18,textAlign:"center"}}>
        <div style={{fontSize:30,marginBottom:8}}>⚠️</div>
        <div style={{fontSize:14,fontWeight:800,marginBottom:8}}>Match in progress</div>
        <div style={{fontSize:12,color:"var(--c-muted)",lineHeight:1.5,marginBottom:16}}>A Match Day match is currently running. Nothing will be lost — every action saves automatically — but you'll be leaving the live match screen. Close anyway?</div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={onCancelCloseWarn} style={{flex:1,padding:"10px",borderRadius:8,border:"1px solid var(--c-border)",background:"none",color:"var(--c-text)",fontWeight:700,fontSize:12,cursor:"pointer"}}>Keep Watching</button>
          <button onClick={onConfirmClose} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:"#ef4444",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>Close Anyway</button>
        </div>
      </div>
    </div>
  )}
  </>);
}

// ── MatchDayPanel ─────────────────────────────────────────────────────────────
// Live match timer with substitutions, tracking every player's time on the
// pitch. State lives in fixtures.match_day (jsonb) and persists on every
// discrete action (start/pause/sub/etc) rather than through the modal's own
// "Save Match" button, since it needs to survive a reload mid-match. Finishing
// writes the final per-player totals into player_match_time for stats.
//
// Clock model: nothing ticks server-side — clockElapsedSeconds/totalSeconds
// are checkpoints, and clockRunningSince/spellStartAt are wall-clock
// timestamps recording when the current running segment began. Live values
// are always checkpoint + (now - since) while running, so a reload just
// resumes the same math from stored timestamps instead of needing a live
// connection.
export function MatchDayPanel({match,players,tc,token,teamId,orgId,readOnly,onMatchDayUpdate,onMatchDayFinished,onResultCarryOver}){
  const [md,setMd]=useState(match.match_day||null);
  const [now,setNow]=useState(Date.now());
  const [subPickerFor,setSubPickerFor]=useState(null);
  const [saving,setSaving]=useState(false);
  const [periodCount,setPeriodCountState]=useState(2);
  const [periodMins,setPeriodMins]=useState([45,45]);
  const [showResetConfirm,setShowResetConfirm]=useState(false);

  useEffect(()=>{ setMd(match.match_day||null); },[match.id]);
  useEffect(()=>{
    if(md?.status!=="running")return;
    const t=setInterval(()=>setNow(Date.now()),1000);
    return ()=>clearInterval(t);
  },[md?.status]);

  async function persist(next){
    setSaving(true);
    setMd(next);
    await api.patch(token,"fixtures",match.id,{match_day:next});
    if(onMatchDayUpdate)onMatchDayUpdate(match.id,next);
    setSaving(false);
  }

  function setPeriodCount(n){
    n=Math.max(1,Math.min(8,parseInt(n)||1));
    setPeriodCountState(n);
    setPeriodMins(prev=>{
      const next=[...prev];
      while(next.length<n)next.push(next[next.length-1]||45);
      next.length=n;
      return next;
    });
  }

  const startingIds=(match.slots||[]).map(s=>s.playerId).filter(Boolean);
  const benchIdsFromLineup=match.subs||[];
  const playerById=Object.fromEntries(players.map(p=>[p.id,p]));

  async function startMatch(){
    const periods=periodMins.map(m=>({lengthMinutes:Math.max(1,parseInt(m)||45)}));
    const playersMap={};
    const nowIso=new Date().toISOString();
    startingIds.forEach(pid=>{playersMap[pid]={onPitch:true,spellStartAt:nowIso,totalSeconds:0};});
    benchIdsFromLineup.forEach(pid=>{if(!playersMap[pid])playersMap[pid]={onPitch:false,spellStartAt:null,totalSeconds:0};});
    await persist({periods,status:"running",currentPeriodIndex:0,clockElapsedSeconds:0,clockRunningSince:nowIso,players:playersMap,subsLog:[]});
  }

  // `atMs` lets a caller checkpoint as of a specific moment rather than the
  // real current time — used by the auto-stop effect below to cap a period
  // at exactly its target length instead of whatever huge wall-clock gap has
  // built up (e.g. after the browser was closed mid-match for hours).
  function checkpointAll(base,atMs){
    const nowMs=atMs??Date.now();
    const nextPlayers={};
    Object.entries(base.players).forEach(([pid,p])=>{
      if(p.onPitch&&p.spellStartAt){
        const add=Math.max(0,(nowMs-new Date(p.spellStartAt).getTime())/1000);
        nextPlayers[pid]={...p,totalSeconds:(p.totalSeconds||0)+add,spellStartAt:null};
      } else nextPlayers[pid]={...p};
    });
    const clockAdd=base.status==="running"&&base.clockRunningSince?Math.max(0,(nowMs-new Date(base.clockRunningSince).getTime())/1000):0;
    return {...base,players:nextPlayers,clockElapsedSeconds:(base.clockElapsedSeconds||0)+clockAdd,clockRunningSince:null};
  }

  async function pauseMatch(){ await persist({...checkpointAll(md),status:"paused"}); }
  async function resumeMatch(){
    const nowIso=new Date().toISOString();
    const nextPlayers={};
    Object.entries(md.players).forEach(([pid,p])=>{ nextPlayers[pid]=p.onPitch?{...p,spellStartAt:nowIso}:{...p}; });
    await persist({...md,status:"running",clockRunningSince:nowIso,players:nextPlayers});
  }
  async function endPeriod(){ await persist({...checkpointAll(md),status:"break"}); }
  async function startNextPeriod(){
    const nowIso=new Date().toISOString();
    const nextPlayers={};
    Object.entries(md.players).forEach(([pid,p])=>{ nextPlayers[pid]=p.onPitch?{...p,spellStartAt:nowIso}:{...p}; });
    await persist({...md,status:"running",currentPeriodIndex:md.currentPeriodIndex+1,clockRunningSince:nowIso,players:nextPlayers});
  }
  // `precomputedMd` lets the auto-stop effect below pass in an already-capped
  // checkpoint (see checkpointAll's `atMs` param) instead of checkpointing
  // against the real current time — otherwise a match auto-finished after
  // sitting "running" for hours (browser closed mid-match) would save
  // wildly inflated playing time.
  async function finishMatch(precomputedMd){
    const finalMd=precomputedMd?{...precomputedMd,status:"finished"}:{...checkpointAll(md),status:"finished"};
    await persist(finalMd);
    const secondsByPlayer={};
    const rows=Object.entries(finalMd.players).filter(([,p])=>(p.totalSeconds||0)>0).map(([pid,p])=>{
      secondsByPlayer[pid]=Math.round(p.totalSeconds||0);
      return {fixture_id:match.id,team_id:teamId,org_id:orgId,player_id:pid,seconds_played:Math.round(p.totalSeconds||0),updated_at:new Date().toISOString()};
    });
    for(const row of rows){
      const existing=await api.get(token,"player_match_time",`fixture_id=eq.${match.id}&player_id=eq.${row.player_id}`);
      if(existing&&existing[0])await api.patch(token,"player_match_time",existing[0].id,{seconds_played:row.seconds_played,updated_at:row.updated_at});
      else await api.post(token,"player_match_time",[row]);
    }
    // Carry goals/assists/score over and durably mark the fixture as played —
    // this writes straight to the fixtures/fixture_events tables (not just
    // local component state) so the result survives the modal closing or
    // remounting. The result counts immediately: no separate Save Match
    // click needed. onMatchDayFinished (below) takes care of bumping season
    // stats, achievements and notifications, the same things a manual Save
    // Match would do.
    const events=md.events||{};
    const ourScore=Object.values(events).reduce((s,e)=>s+(e.goals||0),0);
    const oppScore=md.oppScore||0;
    const scorers=Object.entries(events).filter(([,e])=>e.goals>0).map(([pid,e])=>({playerId:pid,count:e.goals}));
    const assisters=Object.entries(events).filter(([,e])=>e.assists>0).map(([pid,e])=>({playerId:pid,count:e.assists}));

    const ex=await api.get(token,"fixture_events","fixture_id=eq."+match.id); // read before overwrite, so stat updates can be delta-based
    await api.patch(token,"fixtures",match.id,{played:true,our_score:ourScore,opp_score:oppScore});
    for(const e of (ex||[]))await api.del(token,"fixture_events",e.id);
    const evts=[...scorers.map(s=>({fixture_id:match.id,player_id:s.playerId,type:"goal",count:s.count})),...assisters.map(a=>({fixture_id:match.id,player_id:a.playerId,type:"assist",count:a.count}))];
    if(evts.length>0)await api.post(token,"fixture_events",evts);

    if(onMatchDayFinished)onMatchDayFinished(match.id,secondsByPlayer,{played:true,ourScore,oppScore,scorers,assisters,oldEvts:ex});
    if(onResultCarryOver)onResultCarryOver({ourScore,oppScore,scorers,assisters});
  }
  async function resetMatchDay(){ await persist(null); setShowResetConfirm(false); }

  async function doSub(outId,inId){
    const nowIso=new Date().toISOString();
    const running=md.status==="running";
    const outP=md.players[outId]||{onPitch:true,spellStartAt:null,totalSeconds:0};
    const addOut=(outP.onPitch&&outP.spellStartAt)?Math.max(0,(Date.now()-new Date(outP.spellStartAt).getTime())/1000):0;
    const inP=md.players[inId]||{onPitch:false,spellStartAt:null,totalSeconds:0};
    const nextPlayers={...md.players,
      [outId]:{...outP,onPitch:false,spellStartAt:null,totalSeconds:(outP.totalSeconds||0)+addOut},
      [inId]:{...inP,onPitch:true,spellStartAt:running?nowIso:null},
    };
    const subsLog=[...(md.subsLog||[]),{outId,inId,at:nowIso}];
    await persist({...md,players:nextPlayers,subsLog});
    setSubPickerFor(null);
  }

  // Goals/assists recorded during Match Day — kept as a simple {playerId:{goals,assists}}
  // map on match_day itself while the match is live. They get written into the actual
  // match result (fixture_events, our_score/opp_score, played) as soon as Finish Match
  // is clicked — see finishMatch() below — not deferred to a later Save Match click.
  async function addGoal(pid){
    const cur=md.events?.[pid]||{goals:0,assists:0};
    await persist({...md,events:{...(md.events||{}),[pid]:{...cur,goals:cur.goals+1}}});
  }
  async function addAssist(pid){
    const cur=md.events?.[pid]||{goals:0,assists:0};
    await persist({...md,events:{...(md.events||{}),[pid]:{...cur,assists:cur.assists+1}}});
  }
  async function removeGoal(pid){
    const cur=md.events?.[pid]||{goals:0,assists:0};
    if(cur.goals<=0)return;
    await persist({...md,events:{...(md.events||{}),[pid]:{...cur,goals:cur.goals-1}}});
  }
  async function removeAssist(pid){
    const cur=md.events?.[pid]||{goals:0,assists:0};
    if(cur.assists<=0)return;
    await persist({...md,events:{...(md.events||{}),[pid]:{...cur,assists:cur.assists-1}}});
  }
  async function adjustOppScore(delta){
    await persist({...md,oppScore:Math.max(0,(md.oppScore||0)+delta)});
  }

  async function addToBench(pid){
    if(!md||md.players[pid])return;
    await persist({...md,players:{...md.players,[pid]:{onPitch:false,spellStartAt:null,totalSeconds:0}}});
  }
  async function removeFromBench(pid){
    if(!md)return;
    const next={...md.players};
    delete next[pid];
    await persist({...md,players:next});
  }

  // Auto-stop once the live clock reaches the current period's target length,
  // so a match left "running" — because the browser (or tab) was closed
  // mid-match without anyone hitting Pause/End Period/Finish Match — doesn't
  // keep "timing" forever. liveClockSeconds is computed from a persisted
  // start timestamp (see clockRunningSince), so a stale running match can
  // show hours of elapsed time the moment it's reopened; this effect fires
  // immediately on mount (using the initial `now`) as well as on every
  // 1-second tick while actively running, so it catches both cases: a match
  // that's overrun while this panel is open, and one that overran while
  // nobody had it open at all. It caps the checkpoint at the exact moment
  // the target was reached (not "now"), so neither the team clock nor any
  // on-pitch player's time on the pitch include the runaway gap.
  const autoStoppingRef=useRef(false);
  useEffect(()=>{
    // Same malformed-match_day guard as the render path below — this effect runs before
    // that check, so without it a bad row would throw here first.
    if(readOnly||!md||md.status!=="running")return;
    if(!Array.isArray(md.periods)||!md.players||typeof md.players!=="object")return;
    const period=md.periods[md.currentPeriodIndex];
    const targetSecs=(period?.lengthMinutes||45)*60;
    if(liveClockSeconds(md,now)<targetSecs)return;
    if(autoStoppingRef.current)return;
    autoStoppingRef.current=true;
    const cutoffMs=md.clockRunningSince
      ?new Date(md.clockRunningSince).getTime()+Math.max(0,(targetSecs-(md.clockElapsedSeconds||0))*1000)
      :Date.now();
    const capped=checkpointAll(md,cutoffMs);
    const isLastPeriod=md.currentPeriodIndex>=md.periods.length-1;
    const done=isLastPeriod?finishMatch(capped):persist({...capped,status:"break"});
    done.finally(()=>{autoStoppingRef.current=false;});
  },[md,now,readOnly]);

  // ── Setup screen ──────────────────────────────────────────────────────────
  // Every screen below this point reads md.players and md.periods, so anything that
  // isn't a properly-formed match_day object has to be caught here rather than blowing
  // up mid-render with "Cannot convert undefined or null to object". A row can end up
  // malformed in ways this component can't repair — a jsonb value that came back as a
  // string, a half-written record, a fixture saved by an older build — and in every one
  // of those cases the only useful thing to offer is the setup screen, where the coach
  // can just start the match again. Falling back is strictly better than a blank page:
  // no playing time has been lost, because that lives in player_match_time.
  const mdUsable=md&&typeof md==="object"&&md.players&&typeof md.players==="object"&&Array.isArray(md.periods);
  if(!md||md.status==="setup"||!mdUsable){
    return(
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div style={{background:"var(--c-bg0)",borderRadius:9,padding:12,fontSize:12,color:"var(--c-muted)"}}>
          Set up periods, then start the match to begin tracking every player's time on the pitch. This uses whoever's assigned in the Lineup tab as your starting XI and substitutes.
        </div>
        {startingIds.length===0?(
          <div style={{background:"#f59e0b22",border:"1px solid #f59e0b55",borderRadius:9,padding:12,fontSize:12,color:"#f59e0b"}}>
            No starting lineup assigned yet — go to the Lineup tab and place players on the pitch first.
          </div>
        ):(<>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:600,color:"var(--c-text)",marginBottom:6,textTransform:"uppercase"}}>Number of periods</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
              {[1,2,3,4].map(n=><button key={n} onClick={()=>setPeriodCount(n)} style={{padding:"7px 14px",borderRadius:7,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,background:periodCount===n?tc:"var(--c-bg0)",color:periodCount===n?"#fff":"var(--c-muted)"}}>{n}</button>)}
              <input type="number" min={1} max={8} value={periodCount} onChange={e=>setPeriodCount(e.target.value)} style={{width:56,padding:"7px 9px",borderRadius:7,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:12}}/>
            </div>
          </div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:600,color:"var(--c-text)",marginBottom:6,textTransform:"uppercase"}}>Length per period (minutes)</label>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {periodMins.map((m,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:11,color:"var(--c-muted)",width:64}}>Period {i+1}</span>
                  <input type="number" min={1} value={m} onChange={e=>setPeriodMins(prev=>prev.map((v,idx)=>idx===i?e.target.value:v))} style={{width:70,padding:"7px 9px",borderRadius:7,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:12}}/>
                  <span style={{fontSize:11,color:"var(--c-muted)"}}>min</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",marginBottom:6}}>Starting ({startingIds.length})</div>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {startingIds.map(pid=>{const p=playerById[pid];return p?<div key={pid} style={{fontSize:12,padding:"6px 9px",background:"var(--c-bg0)",borderRadius:6}}>{p.name} <span style={{color:"var(--c-muted)",fontSize:10}}>{p.position}</span></div>:null;})}
              </div>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",marginBottom:6}}>Substitutes ({benchIdsFromLineup.length})</div>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {benchIdsFromLineup.length===0?<div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>None yet.</div>:benchIdsFromLineup.map(pid=>{const p=playerById[pid];return p?<div key={pid} style={{fontSize:12,padding:"6px 9px",background:"var(--c-bg0)",borderRadius:6}}>{p.name} <span style={{color:"var(--c-muted)",fontSize:10}}>{p.position}</span></div>:null;})}
              </div>
            </div>
          </div>
          {!readOnly&&<button onClick={startMatch} disabled={saving} style={{padding:"13px",borderRadius:9,border:"none",background:tc,color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer"}}>▶ Start Match</button>}
        </>)}
      </div>
    );
  }

  // ── Finished screen ──────────────────────────────────────────────────────
  if(md.status==="finished"){
    const rows=Object.entries(md.players).map(([pid,p])=>({pid,p:playerById[pid],secs:p.totalSeconds||0})).filter(r=>r.p).sort((a,b)=>b.secs-a.secs);
    return(
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:"#22c55e22",border:"1px solid #22c55e55",borderRadius:9,padding:12,fontSize:12,color:"#16a34a",fontWeight:600}}>✓ Match finished — playtime has been saved to stats, and the match is marked Played with the score/goals/assists recorded. No need to also hit Save Match on the Result tab — that's only there now if you need to fix a mistake.</div>
        <div style={{border:"1px solid var(--c-border)",borderRadius:9,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 60px 70px",gap:6,padding:"6px 10px",fontSize:9,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",background:"var(--c-bg0)"}}>
            <span>Player</span><span style={{textAlign:"center"}}>Pos</span><span style={{textAlign:"right"}}>Time</span>
          </div>
          {rows.map(r=>(
            <div key={r.pid} style={{display:"grid",gridTemplateColumns:"1fr 60px 70px",gap:6,padding:"7px 10px",alignItems:"center",borderTop:"1px solid var(--c-border)"}}>
              <span style={{fontSize:12,fontWeight:600}}>{r.p.name}</span>
              <span style={{fontSize:10,color:"var(--c-muted)",textAlign:"center"}}>{r.p.position}</span>
              <span style={{fontSize:12,fontWeight:700,textAlign:"right",color:tc}}>{fmtClock(r.secs)}</span>
            </div>
          ))}
        </div>
        {!readOnly&&!showResetConfirm&&<button onClick={()=>setShowResetConfirm(true)} style={{padding:"9px",borderRadius:7,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontWeight:600,fontSize:11,cursor:"pointer"}}>Reset Match Day</button>}
        {showResetConfirm&&(
          <div style={{background:"#ef444422",border:"1px solid #ef444455",borderRadius:9,padding:12}}>
            <div style={{fontSize:11,color:"#ef4444",fontWeight:600,marginBottom:8}}>This clears the live timer state so you can redo it. Already-saved stats stay until you finish again.</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={resetMatchDay} style={{flex:1,padding:"7px",borderRadius:6,border:"none",background:"#ef4444",color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Yes, Reset</button>
              <button onClick={()=>setShowResetConfirm(false)} style={{padding:"7px 12px",borderRadius:6,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Running / paused / break screen ───────────────────────────────────────
  const onPitchIds=Object.entries(md.players).filter(([,p])=>p.onPitch).map(([pid])=>pid);
  const benchIds=Object.entries(md.players).filter(([,p])=>!p.onPitch).map(([pid])=>pid);
  const clockSecs=liveClockSeconds(md,now);
  const period=md.periods[md.currentPeriodIndex];
  const availableToAdd=players.filter(p=>!md.players[p.id]);
  const mdEvents=md.events||{};
  const ourLiveScore=Object.values(mdEvents).reduce((s,e)=>s+(e.goals||0),0);
  const oppLiveScore=md.oppScore||0;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{background:"var(--c-bg0)",borderRadius:9,padding:"16px",textAlign:"center"}}>
        <div style={{fontSize:11,color:"var(--c-muted)",fontWeight:700,textTransform:"uppercase",marginBottom:4}}>
          {md.status==="break"?`Break — after Period ${md.currentPeriodIndex+1}`:`Period ${md.currentPeriodIndex+1} of ${md.periods.length}`}
        </div>
        <div style={{fontSize:38,fontWeight:800,color:tc,fontVariantNumeric:"tabular-nums",lineHeight:1.1}}>{fmtClock(clockSecs)}</div>
        {md.status!=="break"&&<div style={{fontSize:10,color:"var(--c-muted2)",marginTop:2}}>Target {period?.lengthMinutes||45} min this period</div>}
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:14,marginTop:14}}>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:9,color:"var(--c-muted2)",textTransform:"uppercase",fontWeight:700}}>Us</div>
            <div style={{fontSize:22,fontWeight:800,color:tc}}>{ourLiveScore}</div>
          </div>
          <div style={{fontSize:14,color:"var(--c-muted2)"}}>–</div>
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:9,color:"var(--c-muted2)",textTransform:"uppercase",fontWeight:700}}>Opponent</div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {!readOnly&&<button onClick={()=>adjustOppScore(-1)} style={{width:22,height:22,borderRadius:"50%",border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:13,lineHeight:1}}>−</button>}
              <span style={{fontSize:22,fontWeight:800}}>{oppLiveScore}</span>
              {!readOnly&&<button onClick={()=>adjustOppScore(1)} style={{width:22,height:22,borderRadius:"50%",border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:13,lineHeight:1}}>+</button>}
            </div>
          </div>
        </div>
        {!readOnly&&<div style={{display:"flex",gap:8,justifyContent:"center",marginTop:14,flexWrap:"wrap"}}>
          {md.status==="running"&&<button onClick={pauseMatch} disabled={saving} style={{padding:"9px 16px",borderRadius:8,border:"none",background:"var(--c-border)",color:"var(--c-text)",fontWeight:700,fontSize:12,cursor:"pointer"}}>⏸ Pause</button>}
          {md.status==="paused"&&<button onClick={resumeMatch} disabled={saving} style={{padding:"9px 16px",borderRadius:8,border:"none",background:tc,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>▶ Resume</button>}
          {md.status==="running"&&md.currentPeriodIndex<md.periods.length-1&&<button onClick={endPeriod} disabled={saving} style={{padding:"9px 16px",borderRadius:8,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontWeight:700,fontSize:12,cursor:"pointer"}}>⏹ End Period</button>}
          {md.status==="break"&&<button onClick={startNextPeriod} disabled={saving} style={{padding:"9px 16px",borderRadius:8,border:"none",background:tc,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>▶ Start Period {md.currentPeriodIndex+2}</button>}
          <button onClick={finishMatch} disabled={saving} style={{padding:"9px 16px",borderRadius:8,border:"1px solid #ef444455",background:"none",color:"#ef4444",fontWeight:700,fontSize:12,cursor:"pointer"}}>🏁 Finish Match</button>
        </div>}
      </div>

      <div>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",marginBottom:6}}>On Pitch ({onPitchIds.length})</div>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {onPitchIds.map(pid=>{
            const p=playerById[pid];if(!p)return null;
            const secs=livePlayerSeconds(md.players[pid],now);
            const isPicking=subPickerFor===pid;
            const ev=mdEvents[pid]||{goals:0,assists:0};
            return(
              <div key={pid}>
                <div onClick={()=>!readOnly&&setSubPickerFor(isPicking?null:pid)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",background:isPicking?tc+"22":"var(--c-bg0)",border:isPicking?`1px solid ${tc}`:"1px solid transparent",borderRadius:8,cursor:readOnly?"default":"pointer"}}>
                  <span style={{width:7,height:7,borderRadius:"50%",background:"#22c55e",flexShrink:0}}/>
                  <span style={{flex:1,fontSize:12,fontWeight:600}}>{p.name}{ev.goals>0&&<span style={{marginLeft:6,fontSize:10,color:tc}}>⚽{ev.goals>1?`×${ev.goals}`:""}</span>}{ev.assists>0&&<span style={{marginLeft:5,fontSize:10,color:"#8b5cf6"}}>🅰️{ev.assists>1?`×${ev.assists}`:""}</span>}</span>
                  <span style={{fontSize:10,color:"var(--c-muted)"}}>{p.position}</span>
                  <span style={{fontSize:13,fontWeight:700,color:tc,fontVariantNumeric:"tabular-nums",minWidth:52,textAlign:"right"}}>{fmtClock(secs)}</span>
                </div>
                {isPicking&&(
                  <div style={{marginTop:4,marginLeft:17,background:"var(--c-bg0)",borderRadius:8,padding:8,display:"flex",flexDirection:"column",gap:4}}>
                    <div style={{display:"flex",gap:6,marginBottom:4}}>
                      <button onClick={()=>addGoal(pid)} style={{flex:1,padding:"7px 9px",borderRadius:6,border:"none",background:tc+"22",color:tc,fontSize:11,fontWeight:700,cursor:"pointer"}}>⚽ + Goal{ev.goals>0?` (${ev.goals})`:""}</button>
                      <button onClick={()=>addAssist(pid)} style={{flex:1,padding:"7px 9px",borderRadius:6,border:"none",background:"#8b5cf622",color:"#8b5cf6",fontSize:11,fontWeight:700,cursor:"pointer"}}>🅰️ + Assist{ev.assists>0?` (${ev.assists})`:""}</button>
                    </div>
                    {(ev.goals>0||ev.assists>0)&&<div style={{display:"flex",gap:6,marginBottom:4}}>
                      {ev.goals>0&&<button onClick={()=>removeGoal(pid)} style={{flex:1,padding:"5px 9px",borderRadius:6,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontSize:10,cursor:"pointer"}}>− Undo Goal</button>}
                      {ev.assists>0&&<button onClick={()=>removeAssist(pid)} style={{flex:1,padding:"5px 9px",borderRadius:6,border:"1px solid var(--c-border)",background:"none",color:"var(--c-muted)",fontSize:10,cursor:"pointer"}}>− Undo Assist</button>}
                    </div>}
                    <div style={{fontSize:10,color:"var(--c-muted)",fontWeight:600,marginBottom:2,marginTop:4}}>Substitute with:</div>
                    {benchIds.length===0?<div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>No substitutes available.</div>:benchIds.map(bid=>{const bp=playerById[bid];if(!bp)return null;return(
                      <button key={bid} onClick={()=>doSub(pid,bid)} style={{textAlign:"left",padding:"7px 9px",borderRadius:6,border:"none",background:"var(--c-surface)",color:"var(--c-text)",fontSize:12,cursor:"pointer"}}>{bp.name} <span style={{color:"var(--c-muted)",fontSize:10}}>{bp.position}</span></button>
                    );})}
                    <button onClick={()=>setSubPickerFor(null)} style={{padding:"5px",borderRadius:6,border:"none",background:"none",color:"var(--c-muted)",fontSize:10,cursor:"pointer"}}>Close</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",marginBottom:6}}>Substitutes ({benchIds.length})</div>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {benchIds.length===0?<div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>None.</div>:benchIds.map(pid=>{
            const p=playerById[pid];if(!p)return null;
            const secs=livePlayerSeconds(md.players[pid],now);
            return(
              <div key={pid} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",background:"var(--c-bg0)",borderRadius:8}}>
                <span style={{width:7,height:7,borderRadius:"50%",background:"var(--c-muted2)",flexShrink:0}}/>
                <span style={{flex:1,fontSize:12,fontWeight:600}}>{p.name}</span>
                <span style={{fontSize:10,color:"var(--c-muted)"}}>{p.position}</span>
                <span style={{fontSize:13,fontWeight:700,color:"var(--c-muted)",fontVariantNumeric:"tabular-nums",minWidth:52,textAlign:"right"}}>{fmtClock(secs)}</span>
                {!readOnly&&secs===0&&<button onClick={()=>removeFromBench(pid)} title="Remove" style={{background:"none",border:"none",color:"var(--c-muted2)",cursor:"pointer",fontSize:14,lineHeight:1}}>×</button>}
              </div>
            );
          })}
        </div>
      </div>

      {!readOnly&&availableToAdd.length>0&&(
        <div>
          <select onChange={e=>{if(e.target.value)addToBench(e.target.value);e.target.value="";}} defaultValue="" style={{width:"100%",padding:"9px 11px",borderRadius:7,border:"1px solid var(--c-border)",background:"var(--c-bg0)",color:"var(--c-text)",fontSize:12,cursor:"pointer"}}>
            <option value="">+ Add player to substitutes…</option>
            {availableToAdd.map(p=><option key={p.id} value={p.id}>{p.name} ({p.position})</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

// ── RatingTimeline ───────────────────────────────────────────────────────────
// Simple hand-drawn SVG line chart (no charting library is loaded in this app)
// showing a player's self-ratings vs coach ratings across matches over time.

export function RatingTimeline({points,ac}){
  if(points.length===0)return <div style={{padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No ratings yet — they'll show up here once a match gets rated.</div>;
  const W=320,H=166,padL=20,padR=8,padT=10,padB=30;
  const innerW=W-padL-padR,innerH=H-padT-padB;
  const n=points.length;
  const xFor=i=>n===1?padL+innerW/2:padL+(innerW*i)/(n-1);
  const yFor=v=>padT+innerH-((v-1)/9)*innerH;
  const labelEvery=Math.max(1,Math.ceil(n/6));
  function shortDate(d){return new Date(d).toLocaleDateString([],{day:"numeric",month:"short"});}
  function linePath(key){
    let d="",started=false;
    points.forEach((p,i)=>{
      if(p[key]==null){started=false;return;}
      d+=(started?"L":"M")+xFor(i)+","+yFor(p[key])+" ";
      started=true;
    });
    return d.trim();
  }
  return(
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block"}}>
        {[1,4,7,10].map(v=><line key={v} x1={padL} x2={W-padR} y1={yFor(v)} y2={yFor(v)} stroke="var(--c-border)" strokeWidth="0.5"/>)}
        {[1,4,7,10].map(v=><text key={v} x={padL-4} y={yFor(v)+2} fontSize="6" fill="var(--c-muted)" textAnchor="end">{v}</text>)}
        <path d={linePath("coach")} fill="none" stroke="#8b5cf6" strokeWidth="1.5"/>
        <path d={linePath("player")} fill="none" stroke={ac} strokeWidth="1.5"/>
        {points.map((p,i)=>(<React.Fragment key={i}>
          {p.coach!=null&&<circle cx={xFor(i)} cy={yFor(p.coach)} r="2" fill="#8b5cf6"/>}
          {p.player!=null&&<circle cx={xFor(i)} cy={yFor(p.player)} r="2" fill={ac}/>}
        </React.Fragment>))}
        {points.map((p,i)=>(i%labelEvery===0||i===n-1)&&p.date?(
          <text key={"d"+i} x={xFor(i)} y={H-padB+11} fontSize="6" fill="var(--c-muted)" textAnchor="middle">{shortDate(p.date)}</text>
        ):null)}
      </svg>
      <div style={{display:"flex",gap:14,justifyContent:"center",marginTop:6}}>
        <span style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"var(--c-muted)"}}><span style={{width:8,height:8,borderRadius:"50%",background:ac}}/>Your Rating</span>
        <span style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"var(--c-muted)"}}><span style={{width:8,height:8,borderRadius:"50%",background:"#8b5cf6"}}/>Coach's Rating</span>
      </div>
    </div>
  );
}

// ── CoachPlayerStatsView ─────────────────────────────────────────────────────
// Shows a coach the same individual stats a player sees on their own account:
// goals/assists, achievements, attendance vs team average, rating averages,
// and the rating timeline. Fetches its own ratings/achievements data; attendance
// is computed from the sessions/roster already loaded for the active team.

export function CoachPlayerStatsView({player,team,sessions,fixtures,token,coachId,ac,AS,onBack,onPlayerPhotoUpdated,featAchievements=true,featPlayerDev=true,featPlayerRatings=true,featMatchDay=false}){
  const [ratings,setRatings]=useState(null);
  const [achievements,setAchievements]=useState(null);
  const [matchTimeRows,setMatchTimeRows]=useState(null); // this player's own player_match_time rows only — self-fetched so every caller (coach/parent/player views) automatically only ever sees this one player's playtime
  useEffect(()=>{
    api.get(token,"match_ratings",`player_id=eq.${player.id}`).then(rows=>setRatings(rows||[]));
    api.get(token,"player_achievements",`player_id=eq.${player.id}`).then(rows=>setAchievements(rows||[]));
    if(featMatchDay)api.get(token,"player_match_time",`player_id=eq.${player.id}`).then(rows=>setMatchTimeRows(rows||[]));
  },[player.id]);

  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  function pctOf(b){ return b.total>0?Math.round((b.present/b.total)*100):null; }
  const myAtt=(()=>{
    const buckets={game:{total:0,present:0},training:{total:0,present:0}};
    sessions.forEach(s=>{
      const status=s.attendance?.[player.id]||"absent";
      const b=s.type==="game"?buckets.game:buckets.training;
      b.total++;
      if(status==="present"||status==="late")b.present++;
    });
    return{match:pctOf(buckets.game),training:pctOf(buckets.training),overall:pctOf({total:buckets.game.total+buckets.training.total,present:buckets.game.present+buckets.training.present})};
  })();
  const teamAtt=(()=>{
    const buckets={game:{total:0,present:0},training:{total:0,present:0}};
    sessions.forEach(s=>{
      const b=s.type==="game"?buckets.game:buckets.training;
      (team.playerIds||[]).forEach(pid=>{
        const status=s.attendance?.[pid]||"absent";
        b.total++;
        if(status==="present"||status==="late")b.present++;
      });
    });
    return{match:pctOf(buckets.game),training:pctOf(buckets.training),overall:pctOf({total:buckets.game.total+buckets.training.total,present:buckets.game.present+buckets.training.present})};
  })();
  const avgOf=arr=>arr.length>0?Math.round((arr.reduce((a,b)=>a+b,0)/arr.length)*10)/10:null;
  const avgCoachRating=ratings?avgOf(ratings.filter(r=>r.source==="coach").map(r=>r.rating)):null;
  const avgSelfRating=ratings?avgOf(ratings.filter(r=>r.source==="player").map(r=>r.rating)):null;
  const playedMatches=fixtures.filter(f=>!f.matches&&f.played);
  const timelinePoints=ratings?[...playedMatches].sort((a,b)=>new Date(a.date)-new Date(b.date)).filter(m=>ratings.some(r=>r.fixture_id===m.id)).map(m=>({date:m.date,player:ratings.find(r=>r.fixture_id===m.id&&r.source==="player")?.rating??null,coach:ratings.find(r=>r.fixture_id===m.id&&r.source==="coach")?.rating??null})):[];

  // Which of the sections below (coach/parent/player can all reach this view) are
  // currently visible — defaults to "All" every time the page loads.
  const statOptionsAll=[{id:"goalsAssists",label:"Goals & Assists"},featAchievements&&{id:"achievements",label:"Achievements"},featPlayerDev&&{id:"playerDevelopment",label:"Player Development"},{id:"attendance",label:"Attendance"},featPlayerRatings&&{id:"performanceRatings",label:"Performance Ratings"},featPlayerRatings&&{id:"ratingTimeline",label:"Rating Timeline"},featMatchDay&&{id:"matchTime",label:"Match Time"}].filter(Boolean);
  const [visibleStats,setVisibleStats]=useStatsVisibility(statOptionsAll);
  // This player's own playtime only — never other players' — per match that has saved Match Day data.
  const myMatchTimeRows=(matchTimeRows||[]).map(r=>({fixture:fixtures.find(f=>f.id===r.fixture_id),secs:r.seconds_played})).filter(r=>r.fixture).sort((a,b)=>new Date(a.fixture.date)-new Date(b.fixture.date));
  const myMatchTimeTotal=myMatchTimeRows.reduce((s,r)=>s+r.secs,0);
  const matchAvailableMins=f=>(f.match_day?.periods||[]).reduce((s,p)=>s+(parseInt(p.lengthMinutes)||0),0);
  const myMatchTimeAvailableTotal=myMatchTimeRows.reduce((s,r)=>s+matchAvailableMins(r.fixture),0);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <button onClick={onBack} style={{alignSelf:"flex-start",fontSize:11,fontWeight:700,color:ac,background:"none",border:"none",cursor:"pointer",padding:0}}>← Back to Players</button>
        <StatsFilterMenu options={statOptionsAll} visible={visibleStats} setVisible={setVisibleStats} AS={AS}/>
      </div>
      <div style={{...C,padding:16,display:"flex",alignItems:"center",gap:10}}>
        <PlayerPhotoUploader playerId={player.id} photoUrl={player.photo_url} name={player.name} size={60} ac={ac} token={token} canEdit={true} onUploaded={url=>onPlayerPhotoUpdated&&onPlayerPhotoUpdated(player.id,url)}/>
        <div>
          <div style={{fontWeight:800,fontSize:15}}><Flag value={player.nationality}/> {player.name}</div>
          <div style={{fontSize:11,color:"var(--c-muted)"}}>{player.position}</div>
        </div>
      </div>
      {visibleStats.has("goalsAssists")&&<div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:9}}>
        {[["Goals",player.goals||0,ac],["Assists",player.assists||0,"#8b5cf6"]].map(([l,v,c])=>(
          <div key={l} style={{...C,padding:13,textAlign:"center"}}>
            <div style={{fontSize:19,fontWeight:700,color:c}}>{v}</div>
            <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{l}</div>
          </div>
        ))}
      </div>}
      {featAchievements&&visibleStats.has("achievements")&&(
      <div>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>🏅 Achievements</div>
        <div style={{...C,padding:14}}>{achievements===null?<div style={{color:"var(--c-muted2)",fontSize:11}}>Loading…</div>:<AchievementBadges rows={achievements} emptyText="No achievements yet."/>}</div>
      </div>
      )}
      {featPlayerDev&&visibleStats.has("playerDevelopment")&&(
      <div>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Player Development</div>
        <div style={{...C,padding:14}}><PlayerDevelopmentSection playerId={player.id} token={token} coachId={coachId} canEdit={true} ac={ac} AS={AS}/></div>
      </div>
      )}
      {visibleStats.has("attendance")&&(
      <div>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Attendance</div>
        <div style={{...C,padding:14,display:"flex",flexDirection:"column",gap:14}}>
          {[["Match",myAtt.match,teamAtt.match],["Training",myAtt.training,teamAtt.training],["Overall",myAtt.overall,teamAtt.overall]].map(([label,mine,teamPct])=>(
            <div key={label}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
                <span style={{fontSize:12,fontWeight:700}}>{label}</span>
                <span style={{fontSize:11,color:"var(--c-muted)"}}>Player: <b style={{color:mine===null?"var(--c-muted2)":mine>=80?"#22c55e":mine>=50?"#eab308":"#ef4444"}}>{mine!==null?`${mine}%`:"—"}</b> <span style={{color:"var(--c-muted2)"}}>· Team avg: {teamPct!==null?`${teamPct}%`:"—"}</span></span>
              </div>
              <div style={{position:"relative",height:7,background:AS.bgColor,borderRadius:4,overflow:"visible"}}>
                <div style={{position:"absolute",left:0,top:0,bottom:0,width:`${mine??0}%`,background:mine===null?"var(--c-muted2)":mine>=80?"#22c55e":mine>=50?"#eab308":"#ef4444",borderRadius:4}}/>
                {teamPct!==null&&<div title={`Team average: ${teamPct}%`} style={{position:"absolute",left:`${teamPct}%`,top:-2,width:2,height:11,background:"var(--c-text2)",transform:"translateX(-1px)"}}/>}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
      {featPlayerRatings&&visibleStats.has("performanceRatings")&&(
      <div>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Performance Ratings (avg)</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:9}}>
          <div style={{...C,padding:13,textAlign:"center"}}>
            <div style={{fontSize:19,fontWeight:700,color:avgCoachRating!==null?ac:"var(--c-muted2)"}}>{avgCoachRating!==null?`${avgCoachRating}/10`:"—"}</div>
            <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>Coach's Rating</div>
          </div>
          <div style={{...C,padding:13,textAlign:"center"}}>
            <div style={{fontSize:19,fontWeight:700,color:avgSelfRating!==null?ac:"var(--c-muted2)"}}>{avgSelfRating!==null?`${avgSelfRating}/10`:"—"}</div>
            <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>Player's Rating</div>
          </div>
        </div>
      </div>
      )}
      {featPlayerRatings&&visibleStats.has("ratingTimeline")&&(
      <div style={{...C,padding:16}}>
        <h3 style={{margin:"0 0 12px",fontSize:11,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Rating Timeline</h3>
        {ratings===null?<div style={{color:"var(--c-muted2)",fontSize:12}}>Loading…</div>:<RatingTimeline points={timelinePoints} ac={ac}/>}
      </div>
      )}
      {featMatchDay&&visibleStats.has("matchTime")&&(
      <div>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>🏟️ Match Time</div>
        <div style={{...C,padding:14}}>
          {matchTimeRows===null?<div style={{color:"var(--c-muted2)",fontSize:11}}>Loading…</div>:myMatchTimeRows.length===0?<div style={{color:"var(--c-muted2)",fontSize:11}}>No Match Day data yet.</div>:(<>
            <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:12}}>
              {myMatchTimeRows.map(r=>{
                const avail=matchAvailableMins(r.fixture);
                return(
                  <div key={r.fixture.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:12,color:"var(--c-text2)"}}>vs {r.fixture.opponent||"TBC"} <span style={{color:"var(--c-muted2)",fontSize:10}}>· {fmtDateShort(r.fixture.date)}</span></span>
                    <span style={{fontSize:12,fontWeight:700,color:ac}}>{fmtMins(r.secs)}{avail>0?<span style={{color:"var(--c-muted2)",fontWeight:600}}> / {avail}'</span>:null}</span>
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4,paddingTop:10,borderTop:`1px solid ${AS.borderColor}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:12,fontWeight:700}}>Total Played</span>
                <span style={{fontSize:14,fontWeight:800,color:ac}}>{fmtMins(myMatchTimeTotal)}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:"var(--c-muted)"}}>Total Available</span>
                <span style={{fontSize:12,fontWeight:700,color:"var(--c-muted)"}}>{myMatchTimeAvailableTotal}'</span>
              </div>
            </div>
          </>)}
        </div>
      </div>
      )}
    </div>
  );
}

export function ReadOnlyPitch({players,slots,subs,tc,strategies,roles,isMobile}){
  const [selectedStrategyId,setSelectedStrategyId]=useState(null);
  if(!slots||slots.length===0)return <div style={{color:"var(--c-muted2)",fontSize:12,textAlign:"center",padding:20}}>No lineup submitted yet.</div>;
  const selectedStrategy=(strategies||[]).find(s=>s.id===selectedStrategyId);
  const subsList=(selectedStrategy?.subs||subs||[]).map(pid=>players.find(p=>p.id===pid)).filter(Boolean);
  const hasStrategies=strategies&&strategies.length>0;
  const hasRoles=roles&&roles.length>0;

  const strategiesSection=hasStrategies&&(
    <div style={isMobile?undefined:{flexShrink:0,width:150}}>
      <div style={{fontSize:9,color:"var(--c-text)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:7}}>🎯 Strategies</div>
      {selectedStrategyId&&(
        <button onClick={()=>setSelectedStrategyId(null)} style={{display:"block",width:"100%",textAlign:"center",padding:"7px 8px",marginBottom:9,borderRadius:6,border:`1px solid ${tc}`,background:"none",color:tc,fontWeight:700,fontSize:11,cursor:"pointer"}}>← Back to Lineup</button>
      )}
      {["attack","defence","general","setpieces"].map(cat=>{
        const list=strategies.filter(s=>s.category===cat);
        if(list.length===0)return null;
        return(
          <div key={cat} style={{marginBottom:8}}>
            <div style={{fontSize:8,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",marginBottom:4}}>{cat==="attack"?"⚔️ Attack":cat==="defence"?"🛡️ Defence":cat==="setpieces"?"🥅 Set Pieces":"📋 General"}</div>
            {list.map(s=>(
              <button key={s.id} onClick={()=>setSelectedStrategyId(id=>id===s.id?null:s.id)} style={{display:"block",width:"100%",textAlign:"left",padding:"6px 8px",marginBottom:3,borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:selectedStrategyId===s.id?tc:"var(--c-bg0)",color:selectedStrategyId===s.id?"#fff":"var(--c-text2)"}}>{s.name}</button>
            ))}
          </div>
        );
      })}
      {selectedStrategy?.notes&&<div style={{fontSize:10,color:"var(--c-muted)",marginTop:6,lineHeight:1.4,padding:"7px 8px",background:"var(--c-bg0)",borderRadius:6}}>{selectedStrategy.notes}</div>}
    </div>
  );
  const rolesSection=hasRoles&&(
    <div style={isMobile?undefined:{flexShrink:0,width:150,marginTop:hasStrategies?14:0}}>
      <div style={{fontSize:9,color:"var(--c-text)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:7}}>Match Roles</div>
      <div style={{display:"flex",flexDirection:"column",gap:5}}>
        {roles.map(([label,name])=>(
          <div key={label} style={{background:"var(--c-bg0)",borderRadius:6,padding:"6px 8px"}}>
            <div style={{fontSize:8,color:"var(--c-muted)",fontWeight:700}}>{label}</div>
            <div style={{fontSize:11,fontWeight:700,marginTop:1}}>{name}</div>
          </div>
        ))}
      </div>
    </div>
  );
  const sideColumn=(hasStrategies||hasRoles)&&(
    <div style={{flexShrink:0,width:isMobile?"100%":150}}>
      {strategiesSection}
      {rolesSection}
    </div>
  );

  return(<>
    <div style={{display:"flex",flexDirection:isMobile?"column":"row",gap:14}}>
      {/* Mobile stacks the pitch vertically, so strategies go above it (and roles below,
          after the pitch). On desktop there is a single side column to the right of the
          pitch — rendering sideColumn here as well as below put an identical copy on
          both sides of the pitch. */}
      {isMobile&&strategiesSection}
      <div style={{flex:1,minWidth:0}}>
        <div style={{position:"relative",width:"100%",paddingBottom:"130%",background:"linear-gradient(180deg,#14532d 0%,#15803d 50%,#14532d 100%)",borderRadius:10,border:"2px solid #15803d",overflow:"hidden"}}>
          <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 100 130" preserveAspectRatio="none">
            <rect x="5" y="4" width="90" height="122" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
            <line x1="5" y1="65" x2="95" y2="65" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
            <circle cx="50" cy="65" r="11" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
            <rect x="25" y="4" width="50" height="16" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.4"/>
            <rect x="25" y="110" width="50" height="16" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.4"/>
          </svg>
          {selectedStrategy&&(
            <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 100 130" preserveAspectRatio="none">
              {(selectedStrategy.drawings||[]).map(d=>renderStaticShape(d,d.id))}
            </svg>
          )}
          {(selectedStrategy?.slots||slots).map(sl=>{
            const pl=players.find(p=>p.id===sl.playerId);
            if(!pl)return null;
            return(
              <div key={sl.id} style={{position:"absolute",left:`${sl.x}%`,top:`${sl.y}%`,transform:"translate(-50%,-50%)",zIndex:10}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:tc,border:`2px solid ${tc}cc`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 6px rgba(0,0,0,0.4)"}}>
                  <span style={{fontSize:11,fontWeight:800,color:"#fff",lineHeight:1}}>{pl.number}</span>
                  <span style={{fontSize:7,color:"var(--c-text3)",maxWidth:34,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"center"}}>{pl.name.split(" ")[0]}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {!isMobile&&sideColumn}
    </div>
    {isMobile&&rolesSection&&<div style={{marginTop:14}}>{rolesSection}</div>}
    {subsList.length>0&&<div style={{marginTop:12}}>
      <div style={{fontSize:9,color:"var(--c-text)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:7}}>Substitutes</div>
      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
        {subsList.map(p=>(
          <div key={p.id} style={{display:"flex",alignItems:"center",gap:5,background:"var(--c-bg0)",borderRadius:6,padding:"4px 9px",border:"1px solid var(--c-border)"}}>
            <span style={{fontSize:9,fontWeight:700,color:"var(--c-muted)"}}>#{p.number}</span>
            <span style={{fontSize:10,fontWeight:600}}>{p.name.split(" ")[0]}</span>
          </div>
        ))}
      </div>
    </div>}
  </>);
}

// ── AuthScreen ────────────────────────────────────────────────────────────────
// ── TeamChatView ──────────────────────────────────────────────────────────────
// Shared chat UI for a single team's parent communication channel — used by
// both the coach's multi-team chat screen and the parent's Communication tab.

